import React from "react";
import * as Icon from "react-feather";
import { Landmark } from "lucide-react";
import clsx from "clsx";
import { debounce } from "lodash";

import "./bookkeeping-table.css";
import { toast as alert } from "react-toastify";

import BookkeepingService from "../../../../services/BookkeepingService";
import AFSService from "../../../../services/AFSService";
import ClientService from "../../../../services/ClientService";
import UtilityService from "../../../../services/UtilityService";
import UserService from "../../../../services/UserService";

import WComponent from "../../../common/WComponent";
import SearchInput from "../../../common/search_input/SearchInput";
import Button from "../../../common/button/Button";
import Loader from "../../../common/loader/Loader";
import Checkbox from "../../../common/checkbox/Checkbox";
import TextAreaField from "../../../common/text_area_field/TextAreaField";
import AFSWalkthrough from "../AFSTool/AFSWalkthrough";
import CompilationRunWizard from "../AFSTool/CompilationRunWizard";
import CurrentYearMovementsService from "../../../../services/CurrentYearMovementsService";
import CoaMappingReview from "./CoaMappingReview";
import AllocationReviewModal from "./AllocationReviewModal";
import OpeningBalanceEntry from "./OpeningBalanceEntry";
import ClassifyTransactionModal from "./ClassifyTransactionModal";
import SettingsModal from "./SettingsModal";
import StatementSettingsModal from "../AFSTool/StatementSettingsModal";
import TrialBalanceImportModal from "./TrialBalanceImportModal";
import ManualTransferModal from "./ManualTransferModal";
import AddClassificationModal from "./AddClassificationModal";
import ClassifyMissingFieldsModal from "./ClassifyMissingFieldsModal";
import BulkClassifyModal from "./BulkClassifyModal";
import SubsequentActivityModal from "./SubsequentActivityModal";
import ArchiveTransactionsModal from "./ArchiveTransactionsModal";
import ToolsModal from "../kit/ToolsModal";
import ToolsTooltip from "../kit/ToolsTooltip";

import TAX_CATEGORIES from "../../../../constants/Taxes";

const ROW_HEIGHT = 45;
const ROWS_KEPT_BEYOND_THE_EDGES = 14;
const CHECKBOX_COLUMN_WIDTH = 36;
const MINIMUM_COLUMN_WIDTH = 56;
const CLASSIFY_CONCURRENCY = 5;

class BookkeepingTable extends WComponent {
	constructor(props) {
		super(props);

		this.whenTheLastSaveFinishes = Promise.resolve();
		this.scrollAreaRef = React.createRef();

		this.state = {
			industry: props.initialIndustry || "",
			province: BookkeepingService.getKnownProvinceOrEmpty({ province: props.initialProvince }),
			bankFilter: "",
			bankAccountLinks: [],

			classifyingAi: false,
			classifyProgress: null,
			settlingTheFile: null,
			selectAll: false,
			activeClassificationDropdown: null,
			bulkClassifyModalOpen: false,
			moreMenuOpen: false,
			subsequentActivityOpen: false,
			archiveModalTransactions: null,
			selectedTransferKeys: {},
			transfersWorking: null,
			manualTransferModalOpen: false,
			manualTransferSeed: [],
			search: "",
			classificationFilter: "",
			classifications: [],
			canonicalTaxonomy: [],
			fsMappingReferenceLoaded: false,
			newAccountReview: null,
			clientRequestData: null,
			expandedClassificationRows: new Set(),
			firstRowOnScreen: 0,
			rowsThatFitOnScreen: 40,
			classifyModalTransaction: null,
			classifyModalSearch: "",
			classifyMissingFieldsModalOpen: false,
			priorYearTrialBalance: [],
			hasTax: props.initialHasTax !== undefined ? props.initialHasTax : true,
			bookkeepingTableHeadings: [
				{ name: "Date", key: "date", className: "bk-table__col--date", width: 100 },
				{ name: "Description", key: "desc", className: "bk-table__col--desc", width: 240 },
				{ name: "Amount", key: "amount", className: "bk-table__col--amount", width: 108 },
				{ name: "Currency", key: "currency", className: "bk-table__col--currency", width: 78 },
				{ name: "Tax", key: "tax", className: "bk-table__col--tax", width: 88 },
				{ name: "Tax Amount", key: "tax-amt", className: "bk-table__col--tax-amt", width: 104 },
				{ name: "Classification", key: "classification", className: "bk-table__col--classification", width: 200 },
				{ name: "FS Account", key: "canonical", className: "bk-table__col--canonical", width: 170 },
				{ name: "Statement", key: "statement", className: "bk-table__col--statement", width: 116 },
				{ name: "Confidence", key: "confidence", className: "bk-table__col--confidence", width: 104 },
				{ name: "Reason", key: "reason", className: "bk-table__col--reason", width: 280 },
				{ name: "Bank", key: "account", className: "bk-table__col--account", width: 180 },
				{ name: "", key: "action", className: "bk-table__col--action", width: 40, fixed: true }
			],
			columnWidths: {},
			hiddenColumns: [],
			resizingColumnKey: "",
			generalLedgerReferences: {},
			generalLedgerEntries: this.props.initialGlEntries || [],
			glQuality: null,
			openAiAccountsTaxClassifications: [],
			classificationTaxMap: {},
			settingsModalOpen: false,
			statementSettingsOpen: false,
			trialBalanceImportOpen: false,
			settingsInitialTab: "accounts",
			autoMappingAccounts: false,
			coaGateOpen: false,
			coaGatePurpose: "afs",
			taxSettingsLoading: false,
			addClassificationModalOpen: false,
			customTaxRates: [],
			taxRules: [],
			allocationReviewOpen: false,
			allocationReviewInitialView: "review",

			loading: false,
			sortField: null,
			sortOrder: "asc",
			activeTab: 'all',
			approveAllModalOpen: false,


			reviewModalOpen: false,
			openingBalancesModalOpen: false,
			openingBalancesPrefill: [],
			openingBalancesLoading: false,
			reviewFeedback: "",
			invertDownloadSigns: false,

			afsActive: false,
			afsLoading: false,
			afsDownloading: false,
			afsBalanceSheet: null,
			afsIncomeStatement: null,
			afsYearEnd: null,
			afsGlEntries: null,
			afsNotes: null,
			afsBlMonthly: null,
			afsAnnotations: null,
			afsClientProfile: null,
			afsPpeNote: null,
			afsRetainedEarnings: null,
			afsIsFinalized: false,

			adjustingEntries: [],
			ajeModalOpen: false,
			persistedNoteTexts: null,
			persistedCustomNotes: [],
			engagementSignedOff: false,

			compilationRunWizardOpen: false,
			clientName: "",
			cwYearEnd: null,
		};
	}

	async componentDidMount() {
		const { clientId, initialCwFiles, initialGlReference, engagementState } = this.props;

		this.serverHasTransactions = BookkeepingService.rememberWhatTheServerHas({ transactions: this.props.transactions });

		const hasGeneralLedgerToApply = !!(initialGlReference && initialGlReference.generalLedgerReference && initialGlReference.generalLedgerReference.length > 0);
		this.generalLedgerPassRunning = hasGeneralLedgerToApply;
		if (hasGeneralLedgerToApply) await this.update({ settlingTheFile: "ledger" });

		this.loadColumnPreferences();
		this.loadBankAccountLinks();

		if (engagementState === "FINALIZED") {
			await this.update({ engagementSignedOff: true });
		}

		let clientChartArrived = false;
		try {
			let coaResult = await BookkeepingService.getClientCoA({ clientId });
			if (coaResult && coaResult.accounts) {
				clientChartArrived = true;
				await this.update({ canonicalTaxonomy: coaResult.accounts });
			}
		} catch (_) {}

		let canonicalTaxonomyArrived = false;
		try {
			let taxonomy = await BookkeepingService.getCanonicalTaxonomy();
			if (taxonomy && taxonomy.length > 0) {
				canonicalTaxonomyArrived = true;
				await this.update({ canonicalTaxonomyOptions: taxonomy });
			}
		} catch (_) {}

		await this.update({ fsMappingReferenceLoaded: clientChartArrived && canonicalTaxonomyArrived });

		try {
			let clientRequestData = await BookkeepingService.getClientRequests({ clientId });
			if (clientRequestData) await this.update({ clientRequestData });
		} catch (_) {}

		const savedCustom = UtilityService.readStoredJson({ key: `bookkeeping_custom_classifications_${clientId}`, fallback: [] });
		const savedTaxMap = UtilityService.readStoredJson({ key: `bookkeeping_tax_map_${clientId}`, fallback: {} });

		const loadedClassifications = [...this.state.classifications, ...savedCustom];
		const classificationsWithSuspense = BookkeepingService.ensureSuspenseClassification({ classifications: loadedClassifications });
		await this.update({
			classifications: classificationsWithSuspense,
			classificationTaxMap: savedTaxMap,
		});

		const customTaxRates = BookkeepingService.getCustomTaxRates({ clientId });
		if (customTaxRates.length > 0) {
			await this.update({ customTaxRates });
		}

		const taxRules = BookkeepingService.getTaxRules({ clientId });
		if (taxRules.length > 0) {
			await this.update({ taxRules });
		}

		let transactionsAfterGeneralLedger = null;

		try {
			if (initialCwFiles && initialCwFiles.length > 0) {
				await this.processReferenceFiles(initialCwFiles);
				if (this.props.cwCurrentYearEnd) {
					this.setState({ cwYearEnd: this.props.cwCurrentYearEnd });
				} else {
					const amFile = initialCwFiles.find(f => f.name.toLowerCase().endsWith("am.dbf"));
					if (amFile) {
						const match = amFile.name.match(/(\d{4}-\d{2}-\d{2})/);
						if (match) this.setState({ cwYearEnd: match[1] });
					}
				}
			} else if (initialGlReference && initialGlReference.generalLedgerReference && initialGlReference.generalLedgerReference.length > 0) {
				try {
					transactionsAfterGeneralLedger = await this.applyGeneralLedgerReference(initialGlReference);
				} catch (error) {
					alert.error("The general ledger reference could not be applied. Classify the transactions manually or re-import the GL.");
				}
			} else {
				const transactions = this.props.transactions || [];
				const areFreshlyGenerated = transactions.length > 0 && transactions.every(transaction => transaction.id == null);
				if (areFreshlyGenerated) await this.saveTransactionsToServer();
			}
		} finally {
			this.generalLedgerPassRunning = false;
		}

		try {
			const { client } = await ClientService.fetchClient({ clientId });
			if (client) this.setState({ clientName: client.name });
		} catch (_) {}

		try {
			let ajeResult = await BookkeepingService.loadAdjustingEntries({ clientId, wpfEngagementId: this.props.wpfEngagementId });
			if (ajeResult && ajeResult.entries && ajeResult.entries.length > 0) {
				await this.update({ adjustingEntries: ajeResult.entries });
			}
		} catch (_) {}

		try {
			let notesResult = await BookkeepingService.loadFsNotes({ clientId, wpfEngagementId: this.props.wpfEngagementId });
			if (notesResult && notesResult.notes && notesResult.notes.length > 0) {
				let noteTexts = {};
				let customNotes = [];
				for (let n of notesResult.notes) {
					if (!n.noteKey) continue;
					if (n.body != null) noteTexts[n.noteKey] = n.body;
					if (n.source === "custom") customNotes.push({ id: n.noteKey, title: n.title || "" });
				}
				await this.update({ persistedNoteTexts: noteTexts, persistedCustomNotes: customNotes });
			}
		} catch (_) {}

		const transactions = transactionsAfterGeneralLedger || this.props.transactions;
		try {
			const theLedgerWasNotAppliedJustNow = transactionsAfterGeneralLedger === null;
			const settledAgainstTheStoredLedger = theLedgerWasNotAppliedJustNow && (await this.settleRowsWithoutAnAccount());

			if (settledAgainstTheStoredLedger) {
				return;
			}
			if (this.canStartAutomaticClassification({ knownTransactions: transactionsAfterGeneralLedger })) {
				await this.startAutomaticClassification({ knownTransactions: transactionsAfterGeneralLedger });
			} else if (transactions && transactions.length > 0 && transactions.some(t => t.canonical_code)) {
				this.autoApplyTaxCodes();
			}
		} finally {
			await this.update({ settlingTheFile: null });
		}
	}

	sayWhyTheProvinceWasNotUnderstood = () => {
		const stored = this.props.initialProvince;
		const wasBlankedAsUnknown = stored && !BookkeepingService.getKnownProvinceOrEmpty({ province: stored });
		if (!wasBlankedAsUnknown || this.warnedAboutTheProvince) return;

		this.warnedAboutTheProvince = true;
		alert.warning(
			`This client's province reads "${stored}", which is not one the tax rates are held against, so nothing has been classified. Pick a province to carry on.`,
			{ autoClose: 12000 }
		);
	};

	canStartAutomaticClassification = ({ knownTransactions = null } = {}) => {
		const { province, industry, classifyingAi } = this.state;
		if (this.generalLedgerPassRunning) return false;
		if (this.automaticClassificationStarted || classifyingAi) return false;
		if (!province || !industry) {
			const thereIsWorkWaiting = BookkeepingService.getTransactionsNeedingClassification({ transactions: knownTransactions || this.props.transactions }).length > 0;
			if (thereIsWorkWaiting) this.sayWhyTheProvinceWasNotUnderstood();
			return false;
		}

		const transactions = knownTransactions || this.props.transactions;
		const stillNeedClassifying = BookkeepingService.getTransactionsNeedingClassification({ transactions });
		return stillNeedClassifying.length > 0;
	};

	startAutomaticClassification = async ({ knownTransactions = null } = {}) => {
		this.automaticClassificationStarted = true;

		const transactions = knownTransactions || this.props.transactions;
		const stillNeedClassifying = BookkeepingService.getTransactionsNeedingClassification({ transactions });

		alert.info(`Classifying the ${stillNeedClassifying.length} transaction${stillNeedClassifying.length === 1 ? "" : "s"} the general ledger did not cover.`, { autoClose: 7000 });
		await this.onClassify(null, { knownTransactions });
	};

	componentDidUpdate(prevProps, prevState) {
		this.returnTheWindowToTheTopWhenTheListChanges({ prevState });

		if (this.props.cwCurrentYearEnd && this.props.cwCurrentYearEnd !== prevProps.cwCurrentYearEnd) {
			this.setState({ cwYearEnd: this.props.cwCurrentYearEnd });
		}

		if (this.props.initialProvince && this.props.initialProvince !== prevProps.initialProvince) {
			this.setState({ province: BookkeepingService.getKnownProvinceOrEmpty({ province: this.props.initialProvince }) });
		}

		const previousCount = (prevProps.transactions || []).length;
		const currentCount = (this.props.transactions || []).length;
		const transactionsArrived = currentCount > previousCount;

		if (transactionsArrived && this.automaticClassificationStarted) {
			this.automaticClassificationStarted = false;
		}

		if (transactionsArrived) {
			this.settleRowsWithoutAnAccount();
		}

		if (this.canStartAutomaticClassification()) {
			this.startAutomaticClassification();
		}
	}

	handleSelectAll = async event => {
		const { checked } = event.target;
		const { transactions, onTransactionsUpdate } = this.props;

		const filteredTransactions = this.getFilteredTransactions();
		const filteredIndexes = this.getFilteredIndexes(filteredTransactions);

		const updatedTransactions = transactions.map((transaction, index) => ({
			...transaction,
			selected: filteredIndexes.has(index) ? checked : transaction.selected
		}));

		await this.update({
			selectAll: checked
		});

		onTransactionsUpdate(updatedTransactions);
	};

	handleSelectRow = async transaction => {
		const { transactions, onTransactionsUpdate } = this.props;

		const updatedTransactions = transactions.map(t => {
			const newSelected = t === transaction ? !t.selected : t.selected;
			return { ...t, selected: newSelected };
		});

		const allSelected = updatedTransactions.every(t => t.selected);

		await this.update({ selectAll: allSelected });

		onTransactionsUpdate(updatedTransactions);
	};

	handleClassificationClick = async (transaction, event) => {
		const { activeClassificationDropdown } = this.state;

		event.stopPropagation();

		await this.update({
			activeClassificationDropdown: activeClassificationDropdown === transaction ? null : transaction
		});
	};

	handleClassificationChange = async (transaction, newClassification) => {
		if (this.theToolIsSettlingTheFile()) return;
		const { classificationTaxMap } = this.state;

		let name = typeof newClassification === "string" ? newClassification : newClassification.name;
		let canonical_code = typeof newClassification === "object" ? newClassification.canonical_code : null;
		let canonical_label = typeof newClassification === "object" ? (newClassification.canonical_label || name) : name;
		let gifi_code = typeof newClassification === "object" ? newClassification.gifi_code : null;

		if (!canonical_code) {
			let match = (this.state.canonicalTaxonomy || []).find(n => (n.display_name || n.accountName) === name);
			if (match) {
				canonical_code = match.canonical_code;
				canonical_label = match.accountName || match.display_name || name;
				gifi_code = match.gifi_code;
			}
		}

		let ruleTax = BookkeepingService.findTaxForTransaction({ transaction, taxRules: this.state.taxRules });
		let autoTax = ruleTax || classificationTaxMap[name] || "";

		if (!autoTax && canonical_code && !canonical_code.startsWith("SUSPENSE.")) {
			try {
				let results = await BookkeepingService.batchTaxCodes({
					province: this.state.province,
					isGstRegistered: true,
					wpfEngagementId: this.props.wpfEngagementId,
					transactions: [{ description: name, canonical_code }],
				});
				if (results && results[0] && results[0].tax_code) {
					autoTax = results[0].tax_code;
					await this.update({ classificationTaxMap: { ...classificationTaxMap, [name]: autoTax } });
				}
			} catch (_) {}
		}

		await this.applyModalEditToFollowingRows({
			transaction,
			changeLabel: name,
			buildPatchForRow: row => this.buildClassificationPatch({ row, name, canonical_code, canonical_label, gifi_code, autoTax, chosenTax: null }),
		});
	};

	manualTaxPatch = ({ tax }) => ({ tax, tax_method: "manual", reviewed_by: null, reviewed_at: null });

	buildClassificationPatch = ({ row, name, canonical_code, canonical_label, gifi_code, autoTax, chosenTax }) => {
		const ruleTax = BookkeepingService.findTaxForTransaction({ transaction: row, taxRules: this.state.taxRules }) || autoTax;
		const { tax, tax_method } = BookkeepingService.taxAfterAnAccountChange({ transaction: row, chosenTax, ruleTax });
		return {
			classification: name,
			classification_label: name,
			account_id: this.getChartAccountIdForName(name),
			canonical_code,
			canonical_label,
			gifi_code,
			classification_method: "manual",
			tax,
			tax_method,
			reviewed_by: null,
			reviewed_at: null,
		};
	};

	applyModalEditToFollowingRows = async ({ transaction, buildPatchForRow, changeLabel }) => {
		const { transactions, onTransactionsUpdate } = this.props;

		const identitiesToChange = this.getIdentitiesFollowingThisClassification({ transaction });
		const clickedIdentity = BookkeepingService.getTransactionIdentity({ transaction });
		const { updatedTransactions, updatedClickedTransaction } = BookkeepingService.applyPatchToRows({
			transactions,
			identitiesToChange,
			buildPatchForRow,
			clickedIdentity,
		});
		if (!updatedClickedTransaction) return;

		if (identitiesToChange.size > 1) {
			alert.info(`${changeLabel} applied to all ${identitiesToChange.size} selected transactions.`, { autoClose: 7000 });
		}

		const openModalIdentity = this.state.classifyModalTransaction
			? BookkeepingService.getTransactionIdentity({ transaction: this.state.classifyModalTransaction })
			: null;
		const modalOpenForThisTransaction = openModalIdentity === clickedIdentity;
		await this.update({
			activeClassificationDropdown: null,
			...(modalOpenForThisTransaction ? { classifyModalTransaction: updatedClickedTransaction } : {}),
		});
		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
	};

	handleTransactionTaxChange = async (transaction, tax) => {
		if (this.theToolIsSettlingTheFile()) return;

		await this.applyModalEditToFollowingRows({
			transaction,
			changeLabel: tax,
			buildPatchForRow: () => this.manualTaxPatch({ tax }),
		});
	};

	getHideableColumns = () => {
		const hideableColumns = this.state.bookkeepingTableHeadings.filter(heading => !heading.fixed);
		return hideableColumns;
	};

	renderBulkSelectionBar = () => {
		const selectedTransactions = this.getSelectedTransactions();
		const hasMultipleSelected = selectedTransactions.length > 1;
		if (!hasMultipleSelected) return null;

		const isViewingArchive = this.state.activeTab === "archived";

		return (
			<div className="bk-bulk__selection-bar">
				<span className="bk-bulk__selection-count">{selectedTransactions.length} transactions selected</span>
				<span className="bk-bulk__selection-spacer" />
				{isViewingArchive ? (
					<Button text="Restore selected" view="secondary" compact onClick={this.onRestoreSelected} icon={<Icon.RotateCcw size={15} />} />
				) : (
					<>
						<Button text="Classify selected" view="secondary" compact onClick={this.openBulkClassifyModal} icon={<Icon.ChevronsDown size={15} />} />
						<Button text="Archive selected" view="secondary" compact onClick={this.openArchiveModal} icon={<Icon.Archive size={15} />} />
					</>
				)}
			</div>
		);
	};

	openArchiveModal = () => {
		const selectedTransactions = this.getSelectedTransactions();
		if (selectedTransactions.length === 0) return;
		this.update({ archiveModalTransactions: selectedTransactions });
	};

	openArchiveModalForRow = transaction => {
		this.update({ archiveModalTransactions: [transaction] });
	};

	closeArchiveModal = () => this.update({ archiveModalTransactions: null });

	onArchiveConfirmed = async ({ reason, note }) => {
		const chosenRows = new Set(this.state.archiveModalTransactions || []);
		await this.update({ archiveModalTransactions: null });
		if (chosenRows.size === 0) return;

		const { transactions, onTransactionsUpdate } = this.props;
		const { transactions: updatedTransactions, archivedCount, transferCounterpartsIncluded } = BookkeepingService.archiveTransactions({
			transactions,
			isChosen: transaction => chosenRows.has(transaction),
			reason,
			note,
			archivedBy: this.getReviewerName(),
			archivedAt: new Date().toISOString(),
		});

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		const transferNote = transferCounterpartsIncluded > 0
			? ` Both sides of a transfer move together, so ${transferCounterpartsIncluded} matching leg${transferCounterpartsIncluded === 1 ? "" : "s"} went too.`
			: "";
		alert.info(`${archivedCount} transaction${archivedCount === 1 ? "" : "s"} archived — out of the books, kept under the Archived tab and restorable.${transferNote}`, { autoClose: 8000 });
	};

	onRestoreSelected = async () => {
		const chosenRows = new Set(this.getSelectedTransactions());
		if (chosenRows.size === 0) return;

		const { transactions, onTransactionsUpdate } = this.props;
		const { transactions: updatedTransactions, restoredCount, transferCounterpartsIncluded } = BookkeepingService.restoreTransactions({
			transactions,
			isChosen: transaction => chosenRows.has(transaction),
		});

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		const transferNote = transferCounterpartsIncluded > 0
			? ` Both sides of a transfer move together, so ${transferCounterpartsIncluded} matching leg${transferCounterpartsIncluded === 1 ? "" : "s"} came back too.`
			: "";
		alert.info(`${restoredCount} transaction${restoredCount === 1 ? "" : "s"} restored to the books.${transferNote}`, { autoClose: 8000 });
	};

	onRestoreRow = async transaction => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { transactions: updatedTransactions } = BookkeepingService.restoreTransactions({
			transactions,
			isChosen: candidate => candidate === transaction,
		});

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		alert.info("Transaction restored to the books.", { autoClose: 6000 });
	};

	isLikelyDuplicate = ({ transaction, duplicateKeys }) => {
		if (transaction.isArchived) return false;
		const key = BookkeepingService.buildDuplicateKeyForTransaction({ transaction });
		const isFlagged = !!key && duplicateKeys.has(key);
		return isFlagged;
	};

	getReviewerName = () => {
		const userData = UserService.getUserData();
		const reviewerName = userData && userData.user ? `${userData.user.first_name || ""} ${userData.user.last_name || ""}`.trim() : "";
		return reviewerName;
	};

	describeTheMatch = ({ transaction }) => {
		const counterpart = (this.props.transactions || []).find(candidate => candidate.id === transaction.mirrors_transaction_id);
		if (!counterpart) return "Matched to the other leg of this transfer — recorded once.";

		const movedOn = counterpart.date ? String(counterpart.date).slice(0, 10) : "";
		const amount = UtilityService.formatCurrency(Math.abs(Number(counterpart.amount) || 0));
		const fromAccount = counterpart.accountName || "the other account";
		return "Matched to the " + amount + " out of " + fromAccount + (movedOn ? " on " + movedOn : "") + " — the same payment, recorded once.";
	};

	renderReasonCell = ({ transaction }) => {
		if (transaction.mirrors_transaction_id) {
			const explanation = this.describeTheMatch({ transaction });
			return (
				<div className="t__row__item bk-table__col--reason">
					<ToolsTooltip content={explanation} className="bk-table__reason-trigger">{explanation}</ToolsTooltip>
				</div>
			);
		}

		const evidence = transaction.source_evidence;
		const reason = evidence && evidence.reason ? evidence.reason : "";
		if (!reason) return <div className="t__row__item bk-table__col--reason">—</div>;

		const lookupNote = evidence.searched ? "Looked up on the web" : evidence.not_searched_because ? `Not looked up — ${evidence.not_searched_because}` : "";
		const tooltip = (
			<>
				<div className="bk-table__reason-tip-text">{reason}</div>
				{lookupNote && <div className="bk-table__reason-tip-note">{lookupNote}</div>}
			</>
		);

		return (
			<div className="t__row__item bk-table__col--reason">
				<ToolsTooltip content={tooltip} className="bk-table__reason-trigger">{reason}</ToolsTooltip>
			</div>
		);
	};

	renderClassificationReason = transaction => {
		const evidence = transaction.source_evidence;
		if (!evidence || !evidence.reason) return null;

		const sources = evidence.sources || [];
		let lookupNote = "";
		if (evidence.searched) lookupNote = "Looked up on the web";
		else if (evidence.not_searched_because) lookupNote = `Not looked up — ${evidence.not_searched_because}`;
		else if (evidence.recorded_before_reasons_were_kept) lookupNote = "Explained from how it was classified — this row predates reasons being recorded";

		return (
			<div className="bk-table__reason">
				<div className="bk-table__reason-text">{evidence.reason}</div>
				<div className="bk-table__reason-meta">
					{lookupNote && <span className="bk-table__reason-note">{lookupNote}</span>}
					{evidence.reused_earlier_answer && <span className="bk-table__reason-note">Reused from an earlier run</span>}
					{sources.map((source, index) => (
						source.url ? (
							<a key={index} className="bk-table__reason-source" href={source.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
								{source.label}
							</a>
						) : (
							<span key={index} className="bk-table__reason-note">{source.label}</span>
						)
					))}
				</div>
			</div>
		);
	};

	getIdentitiesFollowingThisClassification = ({ transaction }) => {
		const clickedIdentity = BookkeepingService.getTransactionIdentity({ transaction });

		const selectedRows = this.getSelectedTransactions();
		const selectedIdentities = new Set(selectedRows.map(row => BookkeepingService.getTransactionIdentity({ transaction: row })));

		const clickedRowIsSelected = selectedIdentities.has(clickedIdentity);
		if (!clickedRowIsSelected || selectedRows.length <= 1) return new Set([clickedIdentity]);

		selectedIdentities.add(clickedIdentity);
		return selectedIdentities;
	};

	getLiveTransactions = () => {
		const rowsThisWasWorkedOutFrom = this.props.transactions;
		const cache = this._liveTransactionsCache;
		if (cache && cache.rowsThisWasWorkedOutFrom === rowsThisWasWorkedOutFrom) return cache.liveTransactions;

		const liveTransactions = (rowsThisWasWorkedOutFrom || []).filter(transaction => !transaction.isArchived);
		this._liveTransactionsCache = { rowsThisWasWorkedOutFrom, liveTransactions };
		return liveTransactions;
	};

	getFiscalYearEndOnRecord = () => {
		const { fiscalPeriod } = this.props;
		const confirmedYearEnd = fiscalPeriod ? fiscalPeriod.fiscalYearEnd : null;
		const confirmedPeriodEnd = fiscalPeriod ? fiscalPeriod.periodEnd : null;
		const yearEndOnRecord = confirmedYearEnd || confirmedPeriodEnd || this.state.cwYearEnd || "";
		return yearEndOnRecord;
	};

	getFiscalYearEndToSeedANewEngagement = () => {
		const engagementAlreadyExists = !!this.props.wpfEngagementId;
		if (engagementAlreadyExists) return undefined;
		return this.getFiscalYearEndOnRecord();
	};

	getProvinceOnRecord = () => {
		const provinceOnRecord = this.state.province || BookkeepingService.getKnownProvinceOrEmpty({ province: this.props.initialProvince });
		return provinceOnRecord;
	};

	getSelectedTransactions = () => {
		const { transactions } = this.props;
		const isViewingArchive = this.state.activeTab === "archived";

		return transactions.filter(transaction => transaction.selected && !!transaction.isArchived === isViewingArchive);
	};

	openBulkClassifyModal = () => {
		const selectedTransactions = this.getSelectedTransactions();
		if (selectedTransactions.length === 0) {
			alert.info("Select the transactions you want to classify first.");
			return;
		}
		this.update({ bulkClassifyModalOpen: true });
	};

	closeBulkClassifyModal = () => {
		this.update({ bulkClassifyModalOpen: false });
	};

	onBulkClassifyApply = async ({ classification, tax }) => {
		await this.update({ bulkClassifyModalOpen: false });
		await this.handleBulkClassificationChange({ newClassification: classification, chosenTax: tax });
	};

	onReviewBulkClassify = async ({ transactions: transactionsToClassify, classification, tax }) => {
		const chosenIdentities = new Set(transactionsToClassify.map(transaction => BookkeepingService.getTransactionIdentity({ transaction })));
		const isChosen = transaction => chosenIdentities.has(BookkeepingService.getTransactionIdentity({ transaction }));

		await this.handleBulkClassificationChange({ newClassification: classification, chosenTax: tax, isChosen });
	};

	handleBulkClassificationChange = async ({ newClassification, chosenTax = null, isChosen = null }) => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { classificationTaxMap, canonicalTaxonomy } = this.state;

		const isSelectedForBulk = transaction => (isChosen ? isChosen(transaction) : transaction.selected && !transaction.isArchived);

		const onlyTheTaxIsChanging = !newClassification;
		if (onlyTheTaxIsChanging) {
			const withTheNewTax = transactions.map(transaction =>
				isSelectedForBulk(transaction) ? { ...transaction, ...this.manualTaxPatch({ tax: chosenTax }) } : transaction);

			onTransactionsUpdate(withTheNewTax);
			await this.saveTransactionsToServer(withTheNewTax);
			await this.forgetWhatWasTicked();
			return;
		}

		let autoTax = classificationTaxMap[newClassification] || "";

		let match = (canonicalTaxonomy || []).find(n => (n.display_name || n.accountName) === newClassification);
		let canonical_code = match ? match.canonical_code : null;
		let canonical_label = match ? (match.accountName || match.display_name || newClassification) : newClassification;
		let gifi_code = match ? match.gifi_code : null;

		if (!autoTax && canonical_code && !canonical_code.startsWith("SUSPENSE.")) {
			try {
				let results = await BookkeepingService.batchTaxCodes({
					province: this.state.province,
					isGstRegistered: true,
					wpfEngagementId: this.props.wpfEngagementId,
					transactions: [{ description: newClassification, canonical_code }],
				});
				if (results && results[0] && results[0].tax_code) {
					autoTax = results[0].tax_code;
					await this.update({ classificationTaxMap: { ...classificationTaxMap, [newClassification]: autoTax } });
				}
			} catch (_) {}
		}

		const rowsAsTheyAreNow = this.props.transactions;
		const identitiesToChange = new Set(
			rowsAsTheyAreNow.filter(isSelectedForBulk).map(transaction => BookkeepingService.getTransactionIdentity({ transaction }))
		);
		const { updatedTransactions } = BookkeepingService.applyPatchToRows({
			transactions: rowsAsTheyAreNow,
			identitiesToChange,
			buildPatchForRow: row => this.buildClassificationPatch({ row, name: newClassification, canonical_code, canonical_label, gifi_code, autoTax, chosenTax }),
			clickedIdentity: null,
		});

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		await this.forgetWhatWasTicked();
	};

	forgetWhatWasTicked = async () => {
		const { transactions, onTransactionsUpdate } = this.props;

		const somethingIsStillTicked = (transactions || []).some(transaction => transaction.selected);
		if (somethingIsStillTicked) {
			await onTransactionsUpdate(transactions.map(transaction => (transaction.selected ? { ...transaction, selected: false } : transaction)));
		}

		await this.update({ selectAll: false });
	};

	handleClickOutside = async () => {
		await this.update({ activeClassificationDropdown: null });
	};

	openSettingsModal = async () => {
		await this.openSettingsModalOnTab("accounts");
	};

	openTaxSettings = async () => {
		await this.openSettingsModalOnTab("taxes");
	};

	onChangeStatementSetting = async ({ setting, value, settings }) => {
		const changes = settings || { [setting]: value };
		const saved = await BookkeepingService.setEngagementPresentationSettings({
			clientId: this.props.clientId,
			wpfEngagementId: this.props.wpfEngagementId,
			settings: changes,
		});
		await this.applyPresentationSettingsToState(saved || {});
		await this.reflectStatementChangesOnScreen({ changes });
	};

	reflectStatementChangesOnScreen = async ({ changes }) => {
		const signature = this.state.compilationReportSignature;
		if (signature) {
			const updatedSignature = { ...signature };
			if (changes.compilationReportCity !== undefined) updatedSignature.city = changes.compilationReportCity;
			if (changes.compilationReportDate !== undefined) updatedSignature.reportDate = changes.compilationReportDate;
			await this.update({ compilationReportSignature: updatedSignature });
		}

		const report = this.state.compilationReport;
		if (report) {
			const updatedReport = { ...report };

			if (changes.compilationReportParagraphs !== undefined) {
				updatedReport.ownParagraphs = changes.compilationReportParagraphs;
			}

			if (changes.useCustomCompilationReport !== undefined) {
				updatedReport.isCustom = !!changes.useCustomCompilationReport;
			}

			updatedReport.paragraphs = updatedReport.isCustom
				? (updatedReport.ownParagraphs || updatedReport.standardParagraphs || [])
				: (updatedReport.standardParagraphs || updatedReport.paragraphs || []);

			await this.update({ compilationReport: updatedReport });
		}

		if (this.state.afsBalanceSheet) {
			await this.rerunChecksAndRefreshStatements({ approvalContext: null });
		}
	};

	onMoveAccount = async ({ section, account, direction }) => {
		const orderedCodes = BookkeepingService.moveAccountWithinSection({
			accounts: section.accounts,
			canonicalCode: account.canonical_code,
			direction,
		});

		const statementOrder = { ...(this.state.statementOrder || {}) };
		statementOrder[section.category] = orderedCodes;

		const sectionSortOrder = { ...(this.state.sectionSortOrder || {}) };
		sectionSortOrder[section.category] = "custom";

		await this.onChangeStatementSetting({ settings: { statementOrder, sectionSortOrder } });
	};

	onSortSection = async ({ section, order }) => {
		const sectionSortOrder = { ...(this.state.sectionSortOrder || {}) };
		const statementOrder = { ...(this.state.statementOrder || {}) };

		if (order === "entered") delete sectionSortOrder[section.category];
		else sectionSortOrder[section.category] = order;

		if (order !== "custom") delete statementOrder[section.category];

		await this.onChangeStatementSetting({ settings: { sectionSortOrder, statementOrder } });
	};

	onRenameAccount = async ({ account, caption }) => {
		const accountCaptions = { ...(this.state.accountCaptions || {}) };
		const wording = (caption || "").trim();

		if (wording) accountCaptions[account.canonical_code] = wording;
		else delete accountCaptions[account.canonical_code];

		await this.onChangeStatementSetting({ setting: "accountCaptions", value: accountCaptions });
	};

	applyPresentationSettingsToState = async settings => {
		let forDisplay = BookkeepingService.describeStoredPresentationSettings({ settings });
		await this.update(forDisplay);
	};

	openSettingsModalOnTab = async initialTab => {
		let storedSettings = await BookkeepingService.getEngagementPresentationSettings({
			clientId: this.props.clientId,
			wpfEngagementId: this.props.wpfEngagementId,
		});
		await this.applyPresentationSettingsToState(storedSettings);
		await this.update({ settingsModalOpen: true, settingsInitialTab: initialTab });

		const { province } = this.state;
		const { transactions } = this.props;
		const allClassifications = this.getSortedClassifications();

		const nameToCanonical = {};
		for (let t of (transactions || [])) {
			if (t.classification && t.canonical_code) nameToCanonical[t.classification] = t.canonical_code;
		}

		let taxableItems = allClassifications
			.filter(c => nameToCanonical[c.name])
			.map(c => ({ description: c.name, canonical_code: nameToCanonical[c.name] }));

		if (taxableItems.length > 0) {
			try {
				let results = await BookkeepingService.batchTaxCodes({
					province,
					isGstRegistered: true,
					wpfEngagementId: this.props.wpfEngagementId,
					transactions: taxableItems,
				});
				if (results && results.length > 0) {
					let updatedMap = { ...this.state.classificationTaxMap };
					for (let r of results) {
						const hasEstablishedRate = !!updatedMap[r.description];
						if (r.tax_code && !hasEstablishedRate) updatedMap[r.description] = r.tax_code;
					}
					await this.update({ classificationTaxMap: updatedMap });
				}
			} catch (_) {}
		}
	};
	closeSettingsModal = () => this.update({ settingsModalOpen: false });

	openTrialBalanceImport = () => this.update({ trialBalanceImportOpen: true });

	closeTrialBalanceImport = () => this.update({ trialBalanceImportOpen: false });

	renderTrialBalanceImportModal = () => {
		if (!this.state.trialBalanceImportOpen) return null;

		return (
			<TrialBalanceImportModal
				clientId={this.props.clientId}
				wpfEngagementId={this.props.wpfEngagementId}
				onClose={this.closeTrialBalanceImport}
			/>
		);
	};

	onSettingsProvinceSelect = async province => {
		await this.update({ province: province.name, provinceChosenByAPerson: true });
		await this.saveTransactionsToServer();
		await this.onFetchAllTaxCodes();
	};

	getTaxOptions = () => {
		const { province, customTaxRates } = this.state;
		const provinceOptions = TAX_CATEGORIES[province] || [];
		const everyProvinceTaxLists = Object.values(TAX_CATEGORIES);
		const everyProvinceTax = everyProvinceTaxLists.flat();
		const baseOptions = provinceOptions.length > 0 ? provinceOptions : everyProvinceTax;
		const combinedOptions = [...baseOptions, ...customTaxRates];
		const uniqueTaxOptions = [...new Set(combinedOptions)];
		return uniqueTaxOptions;
	};

	closeClassifyModal = () => {
		this.update({ classifyModalTransaction: null });
	};

	onCustomTaxRatesChange = customTaxRates => {
		this.update({ customTaxRates });
	};

	onTaxRulesChange = taxRules => {
		BookkeepingService.saveTaxRules({ clientId: this.props.clientId, taxRules });
		this.update({ taxRules });
	};

	onApplyTaxRules = async () => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { taxRules } = this.state;

		const { updatedTransactions, changedCount } = BookkeepingService.applyTaxRulesToUntaxed({ transactions, taxRules });
		if (changedCount === 0) {
			alert.info("No untaxed transactions matched your tax rules.");
			return;
		}

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		alert.success(`Applied tax to ${changedCount} transaction${changedCount === 1 ? "" : "s"}.`);
	};

	openAllocationReview = () => {
		this.update({ allocationReviewOpen: true, allocationReviewInitialView: "review" });
	};

	openIssuesReview = () => {
		this.update({ allocationReviewOpen: true, allocationReviewInitialView: "issues" });
	};

	closeAllocationReview = () => {
		this.update({ allocationReviewOpen: false });
	};

	onOpenOpeningBalances = async () => {
		const { clientId } = this.props;
		await this.update({ openingBalancesModalOpen: true, openingBalancesLoading: true, openingBalancesPrefill: [] });
		const result = await BookkeepingService.getGlOpeningBalanceLines({ clientId });
		const openingBalancesPrefill = result && Array.isArray(result.lines) ? result.lines : [];
		await this.update({ openingBalancesLoading: false, openingBalancesPrefill });
	};

	closeOpeningBalances = () => {
		this.update({ openingBalancesModalOpen: false });
	};

	renderOpeningBalancesModal = () => {
		if (!this.state.openingBalancesModalOpen) return null;

		return (
			<ToolsModal open={true} onClose={this.closeOpeningBalances} title="Opening Balances" width={1080}>
				{this.state.openingBalancesLoading ? (
					<div className="bk-table__ob-loading">Deriving opening balances from the prior-year GL…</div>
				) : (
					<OpeningBalanceEntry
						clientId={this.props.clientId}
						wpfEngagementId={this.props.wpfEngagementId}
						prefillLines={this.state.openingBalancesPrefill}
						hideTitle
						onComplete={this.closeOpeningBalances}
						onClose={this.closeOpeningBalances}
					/>
				)}
			</ToolsModal>
		);
	};

	onReviewTransactionsChange = async updatedTransactions => {
		this.props.onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
	};

	renderAllocationReview = () => {
		if (!this.state.allocationReviewOpen) return null;

		return (
			<AllocationReviewModal
				clientId={this.props.clientId}
				fiscalYearEnd={this.getFiscalYearEndOnRecord()}
				transactions={this.getLiveTransactions()}
				taxOptions={this.getTaxOptions()}
				customTaxRates={this.state.customTaxRates}
				onTaxChange={this.handleTransactionTaxChange}
				onCustomTaxRatesChange={this.onCustomTaxRatesChange}
				onMarkAsTransfer={this.onMarkAsTransferFromReview}
				onBulkClassify={this.onReviewBulkClassify}
				priorTrialBalanceEntries={this.getPriorTrialBalanceEntries()}
				unmappedAccountCount={this.getAccountsWithoutFsMapping().length}
				fsMappingReferenceLoaded={this.state.fsMappingReferenceLoaded}
				onRequestCoaMapping={this.onRequestCoaMapping}
				hasTax={this.taxesApply()}
				classifications={this.getSortedClassifications()}
				classificationTaxMap={this.state.classificationTaxMap}
				generalLedgerEntries={this.getGeneralLedgerEntriesWithCanonicalCode()}
				bankAccountCanonicalCodes={this.getBankAccountCanonicalCodes()}
				initialView={this.state.allocationReviewInitialView}
				onTransactionsChange={this.onReviewTransactionsChange}
				onClose={this.closeAllocationReview}
			/>
		);
	};

	onClassificationsChange = classifications => {
		this.update({ classifications });
	};

	onTaxMapChange = classificationTaxMap => {
		this.update({ classificationTaxMap });
	};

	onAutoMapAccounts = async () => {
		const { onTransactionsUpdate, clientId } = this.props;
		const unmappedNames = this.getAllClientAccountNames().filter(name => !this.getCanonicalLabelForAccount(name));
		if (unmappedNames.length === 0) return;

		await this.update({ autoMappingAccounts: true });

		const result = await BookkeepingService.mapCoaSuggest({
			clientId,
			accounts: unmappedNames.map(name => ({ accountNumber: BookkeepingService.extractAccountNumberFromName(name), accountName: name, gifiCode: null })),
		});
		const suggestions = result && Array.isArray(result.suggestions) ? result.suggestions.filter(s => s.suggested_canonical_code) : [];

		if (suggestions.length === 0) {
			await this.update({ autoMappingAccounts: false });
			alert.info("No FS account matches found for the unmapped accounts.");
			return;
		}

		let remappedTransactions = this.props.transactions;
		for (const suggestion of suggestions) {
			remappedTransactions = BookkeepingService.remapTransactionsToCanonical({
				transactions: remappedTransactions,
				classificationName: suggestion.accountName,
				canonicalNode: { canonical_code: suggestion.suggested_canonical_code, display_name: suggestion.suggested_display_name, gifi_code: suggestion.suggested_gifi_code },
			});
		}
		onTransactionsUpdate(remappedTransactions);

		try {
			await BookkeepingService.mapCoaSave({
				clientId,
				mappings: suggestions.map(s => ({ accountId: this.getChartAccountIdForName(s.accountName), accountNumber: BookkeepingService.extractAccountNumberFromName(s.accountName), accountName: s.accountName, canonical_code: s.suggested_canonical_code, gifiCode: s.suggested_gifi_code || null })),
			});
		} catch (_) {}

		await this.saveTransactionsToServer(remappedTransactions);
		await this.update({ autoMappingAccounts: false });
		alert.success(`Mapped ${suggestions.length} of ${unmappedNames.length} account${unmappedNames.length > 1 ? "s" : ""} to FS accounts.`);
	};

	getAccountsWithoutFsMapping = () => {
		if (!this.state.fsMappingReferenceLoaded) return [];
		return this.getAllClientAccountNames().filter(name => !this.getCanonicalLabelForAccount(name));
	};

	onGenerateAfsClicked = async () => {
		if (!this.state.fsMappingReferenceLoaded) {
			alert.error("Couldn't load this client's FS account mapping, so unmapped accounts can't be checked. Reload the page and try again.");
			return;
		}

		if (this.getAccountsWithoutFsMapping().length > 0) {
			this.update({ coaGateOpen: true, coaGatePurpose: "afs" });
			return;
		}
		const saved = await this.saveTransactionsToServer();
		if (!saved) return;
		this.update({ compilationRunWizardOpen: true });
	};

	onRequestCoaMapping = () => {
		if (this.getAccountsWithoutFsMapping().length === 0) return;
		this.update({ coaGateOpen: true, coaGatePurpose: "trialBalance" });
	};

	onCoaGateComplete = async ({ mappings }) => {
		const { transactions, onTransactionsUpdate } = this.props;

		const nodesByCode = {};
		for (const node of (this.state.canonicalTaxonomyOptions || [])) nodesByCode[node.canonical_code] = node;

		let remappedTransactions = transactions;
		for (const mapping of mappings) {
			const canonicalNode = nodesByCode[mapping.canonical_code] || { canonical_code: mapping.canonical_code, display_name: mapping.canonical_code, gifi_code: mapping.gifiCode || null };
			remappedTransactions = BookkeepingService.remapTransactionsToCanonical({
				transactions: remappedTransactions,
				classificationName: mapping.accountName,
				canonicalNode,
			});
		}
		onTransactionsUpdate(remappedTransactions);
		const saved = await this.saveTransactionsToServer(remappedTransactions);

		if (this.state.coaGatePurpose === "trialBalance") {
			await this.update({ coaGateOpen: false, coaGatePurpose: "afs" });
			return;
		}
		await this.update({ coaGateOpen: false, compilationRunWizardOpen: saved });
	};

	openAddClassificationModal = () => this.update({ addClassificationModalOpen: true });
	closeAddClassificationModal = () => this.update({ addClassificationModalOpen: false });

	onAddAccount = async ({ name, canonicalCode, tax }) => {
		const { classifications, classificationTaxMap } = this.state;
		const { clientId } = this.props;

		const cleanName = UtilityService.toTrimmedText(name);
		if (!cleanName || !canonicalCode) return false;

		const created = await BookkeepingService.createChartOfAccount({ clientId, accountName: cleanName, canonicalCode });
		if (!created || created.error) {
			alert.error((created && created.error) || "The account could not be added to the chart.");
			return false;
		}
		if (created.conflict) {
			alert.error(`Account number ${created.existing.accountNumber} is already on this chart as ${created.existing.accountName}.`);
			return false;
		}

		const account = created.account;
		const updatedMap = { ...classificationTaxMap };
		if (tax) updatedMap[account.accountName] = tax;
		const serializedTaxMap = JSON.stringify(updatedMap);
		localStorage.setItem(`bookkeeping_tax_map_${clientId}`, serializedTaxMap);

		const clientChart = await BookkeepingService.getClientCoA({ clientId });
		const refreshedChart = clientChart && clientChart.accounts ? clientChart.accounts : this.state.canonicalTaxonomy;

		await this.update({
			classifications: [...classifications, { name: account.accountName }],
			classificationTaxMap: updatedMap,
			canonicalTaxonomy: refreshedChart,
		});
		alert.success(`${account.accountName} was added to the chart of accounts.`);
		return true;
	};

	autoApplyTaxCodes = async () => {
		const { transactions, onTransactionsUpdate } = this.props;
		const { province } = this.state;
		if (!transactions || transactions.length === 0 || !province) return;

		let classifiedTxs = transactions.filter(t => t.canonical_code && !t.canonical_code.startsWith("SUSPENSE."));
		if (classifiedTxs.length === 0) return;

		const everyClassifiedRowIsSettled = classifiedTxs.every(transaction => BookkeepingService.aPersonHasDecidedTheTax({ transaction }));
		if (everyClassifiedRowIsSettled) return;

		try {
			let results = await BookkeepingService.batchTaxCodes({
				province,
				industry: this.state.industry,
				isGstRegistered: true,
				wpfEngagementId: this.props.wpfEngagementId,
				transactions: classifiedTxs.map(t => ({
					description: t.description,
					canonical_code: t.canonical_code,
				})),
			});

			let taxLookup = {};
			let taxMethodLookup = {};
			for (let r of results || []) {
				if (!r.tax_code) continue;
				taxLookup[r.description] = r.tax_code;
				taxMethodLookup[r.description] = r.decidedBy === "ledger" ? "ledger" : "model";
			}

			if (Object.keys(taxLookup).length === 0) return;

			let updatedMap = { ...this.state.classificationTaxMap };
			for (let r of results || []) {
				const hasEstablishedRate = !!updatedMap[r.description];
				if (r.tax_code && r.description && !hasEstablishedRate) updatedMap[r.description] = r.tax_code;
			}

			const rowsAsTheyAreNow = this.props.transactions;
			let changed = false;
			let updatedTransactions = rowsAsTheyAreNow.map(t => {
				if (BookkeepingService.aPersonHasDecidedTheTax({ transaction: t })) return t;

				let newTax = taxLookup[t.description];
				if (!newTax) return t;

				changed = true;
				return { ...t, tax: newTax, tax_method: taxMethodLookup[t.description] || "ledger" };
			});

			if (changed) {
				onTransactionsUpdate(updatedTransactions);
				await this.saveTransactionsToServer(updatedTransactions);
			}

			await this.update({ classificationTaxMap: updatedMap });
		} catch (error) {
			alert.error("The tax codes could not be applied. " + (error.message || "Try again, or set them by hand."));
			console.error("autoApplyTaxCodes failed:", error);
		}
	};

	findClientChartRowForAccount = ({ accountName }) => {
		const clientChart = this.state.canonicalTaxonomy || [];
		return BookkeepingService.findChartRowForAccountName({ clientChart, accountName });
	};

	getChartAccountIdForName = accountName => {
		const chartRow = this.findClientChartRowForAccount({ accountName });
		return chartRow && chartRow.id ? chartRow.id : null;
	};

	getCanonicalCodeForAccount = accountName => {
		const chartRow = this.findClientChartRowForAccount({ accountName });
		return chartRow && chartRow.canonical_code ? chartRow.canonical_code : null;
	};

	getGeneralLedgerEntriesWithCanonicalCode = () => {
		const glEntriesWithCanonical = (this.state.generalLedgerEntries || []).map(entry => ({
			...entry,
			canonical_code: entry.canonicalCode || this.getCanonicalCodeForAccount(entry.account),
		}));
		return glEntriesWithCanonical;
	};

	getPriorTrialBalanceEntries = () => {
		const glEntriesWithCanonical = this.getGeneralLedgerEntriesWithCanonicalCode();
		return BookkeepingService.buildPriorTrialBalanceEntries({ generalLedgerEntries: glEntriesWithCanonical });
	};

	applyTaxCodesToAccounts = async () => {
		const { onTransactionsUpdate } = this.props;
		const { province } = this.state;
		if (!province) return;

		const accountItems = this.getUsedClassificationNames()
			.map(name => ({ description: name, canonical_code: this.getCanonicalCodeForAccount(name) }))
			.filter(item => item.canonical_code && !item.canonical_code.startsWith("SUSPENSE."));

		if (accountItems.length === 0) {
			alert.info("Map your accounts to FS accounts first (Accounts tab) — the tax treatment is derived from the FS account.");
			return;
		}

		const results = await BookkeepingService.batchTaxCodes({
			province,
			isGstRegistered: true,
			wpfEngagementId: this.props.wpfEngagementId,
			transactions: accountItems,
		});
		if (!results || results.length === 0) return;

		const taxByAccount = {};
		for (const r of results) {
			if (r.tax_code) taxByAccount[r.description] = r.tax_code;
		}

		const rowsAsTheyAreNow = this.props.transactions;
		const updatedTransactions = rowsAsTheyAreNow.map(t => {
			const rate = taxByAccount[t.classification];
			if (!rate || BookkeepingService.aPersonHasDecidedTheTax({ transaction: t })) return t;
			return { ...t, tax: rate, tax_method: "rule" };
		});
		onTransactionsUpdate(updatedTransactions);
		await this.update({ classificationTaxMap: { ...taxByAccount, ...this.state.classificationTaxMap } });
	};

	onFetchAllTaxCodes = async () => {
		await this.update({ taxSettingsLoading: true });
		await this.applyTaxCodesToAccounts();
		await this.autoApplyTaxCodes();
		await this.update({ taxSettingsLoading: false });
	};

	onBankFilterChange = e => this.update({ bankFilter: e.target.value });

	renderBankFilter = () => {
		const { bankFilter } = this.state;
		const bankNames = BookkeepingService.getUniqueBankAccountNames({ transactions: this.getLiveTransactions() });
		if (bankNames.length < 2) return null;

		return (
			<select className="bk-table__bank-filter" value={bankFilter} onChange={this.onBankFilterChange}>
				<option value="">All banks</option>
				{bankNames.map(name => (
					<option key={name} value={name}>{name}</option>
				))}
			</select>
		);
	};

	getFilteredTransactions = () => {
		const { search, activeTab, bankFilter } = this.state;
		const { transactions } = this.props;

		let filteredTransactions = transactions.filter(t => (activeTab === "archived" ? t.isArchived : !t.isArchived));

		if (bankFilter) {
			filteredTransactions = filteredTransactions.filter(t => t.accountName === bankFilter);
		}

		if (activeTab === 'to-review') {
			filteredTransactions = filteredTransactions.filter(t =>
				BookkeepingService.awaitsReviewerSignOff({ transaction: t })
			);
		} else if (activeTab === 'auto-classified') {
			filteredTransactions = filteredTransactions.filter(t => t.glClassified || t.cwClassified);
		} else if (activeTab === 'approved') {
			filteredTransactions = filteredTransactions.filter(t => t.classification_status === 'approved');
		} else if (activeTab === 'inconsistent') {
			const split = BookkeepingService.findDescriptionsAnsweredMoreThanOneWay({ transactions });
			const splitDescriptions = new Set(split.descriptions);
			filteredTransactions = filteredTransactions.filter(t =>
				t.description && splitDescriptions.has(t.description.trim().toLowerCase())
			);
		} else if (activeTab === 'unclassified') {
			const stillWaiting = new Set(this.getRowsStillNeedingAnAccount({ transactions: filteredTransactions }));
			filteredTransactions = filteredTransactions.filter(t => stillWaiting.has(t));
		} else if (activeTab === 'suspense') {
			filteredTransactions = filteredTransactions.filter(t =>
				(t.canonical_code && t.canonical_code.startsWith('SUSPENSE.')) || (!t.canonical_code && t.classification && t.classification !== 'Unclassified')
			);
		}

		if (search) {
			const term = search.toLowerCase();
			filteredTransactions = filteredTransactions.filter(t =>
				t.description.toLowerCase().includes(term) ||
				(t.merchant || "").toLowerCase().includes(term) ||
				(t.classification || "").toLowerCase().includes(term) ||
				(t.canonical_label || "").toLowerCase().includes(term) ||
				(t.accountName || "").toLowerCase().includes(term) ||
				(t.date || "").includes(term) ||
				String(t.amount).includes(term)
			);
		}

		return filteredTransactions;
	};

	getFilteredIndexes = filteredTransactions => {
		const { transactions } = this.props;

		return new Set(filteredTransactions.map(filteredTransaction => transactions.findIndex(transaction => transaction === filteredTransaction)));
	};

	onExport = async e => {
		if (e && e.preventDefault) e.preventDefault();
		await this.update({ reviewModalOpen: true, sessionEndTime: Date.now() });
	};

	onExportSuspenseClicked = async () => {
		const { clientId, transactions } = this.props;

		const suspenseTransactions = BookkeepingService.getSuspenseTransactions({ transactions });
		if (suspenseTransactions.length === 0) {
			alert.info("There are no suspense or unclassified transactions to export.");
			return;
		}

		await BookkeepingService.downloadSuspenseTransactions({ clientId, transactions });
	};

	getImportedFileNames = () => {
		const liveSourceFiles = this.props.getImportSourceFiles ? this.props.getImportSourceFiles() : [];
		if (liveSourceFiles.length > 0) return liveSourceFiles.map(file => file.name);
		return this.props.statementFileNames || [];
	};

	onConfirmReview = async () => {
		let { clientId, wpfEngagementId, sessionStartTime } = this.props;
		let { priorYearTrialBalance, reviewFeedback, sessionEndTime, invertDownloadSigns } = this.state;

		await this.update({ reviewModalOpen: false, loading: true });

		const archiveSummary = BookkeepingService.buildArchiveSummary({ transactions: this.props.transactions });
		const transactions = this.getLiveTransactions();
		const summary = BookkeepingService.buildClassificationSummary({ transactions });
		const sessionDurationMs = sessionStartTime && sessionEndTime ? sessionEndTime - sessionStartTime : null;

		const periodExclusionSummary = BookkeepingService.buildPeriodExclusionSummary({
			fiscalPeriod: this.props.fiscalPeriod,
			periodExclusions: this.props.periodExclusions,
		});

		const reviewData = {
			reviewFeedback,
			sessionStartTime: sessionStartTime ? new Date(sessionStartTime).toISOString() : null,
			sessionEndTime: sessionEndTime ? new Date(sessionEndTime).toISOString() : null,
			sessionDurationMs,
			csvFileNames: this.getImportedFileNames(),
			fiscalPeriod: this.props.fiscalPeriod || null,
			periodExclusions: periodExclusionSummary,
			archiveSummary,
			classificationSummary: {
				totalCount: summary.totalCount,
				glReferenceCount: summary.glReferenceCount,
				autoClassifiedCount: summary.autoClassifiedCount,
				transferCount: summary.transferCount,
				unclassifiedCount: summary.unclassifiedCount,
				classifiedCount: summary.classifiedCount,
				manualChangeCount: summary.manualChangeCount,
				coveragePercent: summary.coveragePercent,
				suggestionAccuracyPercent: summary.suggestionAccuracyPercent,
			},
			manualChanges: summary.manualChanges.map(t => ({
				description: t.description,
				cwClassification: t.cwClassification || null,
				aiClassification: t.aiClassification || null,
				finalClassification: t.classification,
			})),
		};

		const glEntriesWithCanonical = this.getGeneralLedgerEntriesWithCanonicalCode();
		const accountNumberByCanonicalCode = BookkeepingService.buildAccountNumberByCanonicalCode({ generalLedgerEntries: glEntriesWithCanonical });
		const accountNumberByAccountName = BookkeepingService.buildAccountNumberByAccountName({ generalLedgerEntries: glEntriesWithCanonical });

		const filteredTransactions = transactions.map(transaction => {
			let { date, description, merchant, amount, classification, accountName, tax, cwClassification, aiClassification, canonical_code, clientBankStatementId } = transaction;
			let accountNumber = accountNumberByCanonicalCode[canonical_code] || accountNumberByAccountName[classification] || BookkeepingService.getLeadingAccountNumber({ accountName: classification });
			let bankAccountNumber = accountNumberByAccountName[accountName] || BookkeepingService.getLeadingAccountNumber({ accountName });
			return { date, description, merchant, amount, classification, canonicalCode: canonical_code || null, accountName, tax, accountNumber, bankAccountNumber, cwClassification: cwClassification || null, aiClassification: aiClassification || null, clientBankStatementId: clientBankStatementId || null };
		});

		const sourceFiles = this.props.getImportSourceFiles ? this.props.getImportSourceFiles() : [];

		await BookkeepingService.downloadBookkeepingSummaries({
			clientId,
			wpfEngagementId,
			transactions: filteredTransactions,
			priorYearTrialBalance: priorYearTrialBalance || [],
			reviewData,
			invertSigns: invertDownloadSigns,
			sourceFiles,
		});

		await this.update({ loading: false });
	};

	classifyEveryTransactionTheLedgerMissed = async (e, { knownTransactions = null } = {}) => {
		if (e && e.preventDefault) e.preventDefault();
		if (this.state.classifyingAi) return;

		if (!this.state.province || !this.state.industry) {
			await this.update({ classifyMissingFieldsModalOpen: true });
			return;
		}

		const { clientId, onTransactionsUpdate } = this.props;
		const transactions = knownTransactions || this.props.transactions;
		const { classifications, industry, province, generalLedgerReferences } = this.state;
		const glRefs = generalLedgerReferences || {};

		const INCOME_CLASSES = new Set(['SA', 'OI', 'DI']);
		const EXPENSE_CLASSES = new Set(['OE', 'OD', 'IT', 'COGS']);
		const filterClassificationsByAmount = (list, amount) => {
			if (amount == null || isNaN(amount)) return list;
			const isExpense = amount > 0;
			return list.filter(c => {
				if (!c.accountType || c.accountType === 'B') return true;
				if (!c.accountClass) return true;
				return isExpense ? !INCOME_CLASSES.has(c.accountClass) : !EXPENSE_CLASSES.has(c.accountClass);
			});
		};

		const preClassified = BookkeepingService.clearClassificationsThatNameNoAccount({ transactions });
		const preClassifiedCount = preClassified.filter((tx, i) => tx.classification !== transactions[i]?.classification).length;
		if (preClassifiedCount > 0) onTransactionsUpdate(preClassified);

		const { classifiedTransactions, unclassifiedTransactions } = BookkeepingService.getClassifiedAndUnclassifiedTransactions({ transactions: preClassified });
		if (unclassifiedTransactions.length === 0) return;

		const unclassifiedDescriptions = new Set(unclassifiedTransactions.map(tx => tx.description));
		let workingTransactions = preClassified.map(tx =>
			unclassifiedDescriptions.has(tx.description) ? { ...tx, aiClassifying: true } : tx
		);
		onTransactionsUpdate(workingTransactions);
		await this.update({ classifyingAi: true });

		const aiTaxMap = { ...this.state.classificationTaxMap };

		// Built once per row rather than recomputed on every comparison — the answer is
		// applied by scanning all rows once per question, which would otherwise rebuild
		// every key for every question.
		const questionKeyByTransaction = new Map();
		const askingKeyFor = transaction => {
			if (!questionKeyByTransaction.has(transaction)) {
				questionKeyByTransaction.set(transaction, BookkeepingService.buildClassificationQuestionKey({ description: transaction.description, amount: transaction.amount }));
			}
			return questionKeyByTransaction.get(transaction);
		};

		const firstRowOfEachQuestion = new Map();
		for (const transaction of unclassifiedTransactions) {
			const questionKey = askingKeyFor(transaction);
			if (!firstRowOfEachQuestion.has(questionKey)) firstRowOfEachQuestion.set(questionKey, transaction);
		}
		const needsAi = [...firstRowOfEachQuestion.values()];
		this.questionsAnswered = 0;
		await this.update({ classifyProgress: { done: 0, total: needsAi.length } });

		let classificationReferences = classifiedTransactions.map(({ classification, description }) => ({ classification, description }));
		const resultCache = {};
		const normalizeDescription = desc => (desc || '').replace(/\s*\(\d+\)\s*$/, '').trim();

		const countOneQuestionAnswered = () => {
			this.questionsAnswered += 1;
			this.update({ classifyProgress: { done: this.questionsAnswered, total: needsAi.length } });
		};

		const classifyOneDescription = async transaction => {
			const desc = transaction.description;
			const questionKey = askingKeyFor(transaction);
			const isTheSameQuestion = row => askingKeyFor(row) === questionKey;
			const cacheKey = questionKey;

			try {
				let result = resultCache[cacheKey];
				if (!result) {
					result = await BookkeepingService.categorizeTransaction({
						clientId,
						transactionDescription: normalizeDescription(desc) + " for an amount of $" + transaction.amount,
						amount: transaction.amount,
						classifications: filterClassificationsByAmount(classifications, transaction.amount),
						classificationReferences,
						industry,
						province,
						generalLedgerReferences: glRefs
					});
				}

				const modelUnavailableBecause = result?.source_evidence?.ai_unavailable_because;
				if (modelUnavailableBecause) {
					this.modelUnavailableBecause = modelUnavailableBecause;
					this.descriptionsTheModelCouldNotReach += 1;
				}

				const rawConfidence = result?.confidence;
				const confidence = rawConfidence != null ? (rawConfidence > 1 ? rawConfidence / 100 : rawConfidence) : null;
				const classification = result?.classification || "Unclassified";
				const namedARealAccount = classification !== "Unclassified";
				const tax = aiTaxMap[classification] || "";

				if (classification && classification !== "Unclassified") {
					if (!resultCache[cacheKey]) resultCache[cacheKey] = result;
					classificationReferences = [...classificationReferences, { classification, description: desc }];
				}

				workingTransactions = workingTransactions.map(wt =>
					isTheSameQuestion(wt) && wt.aiClassifying ? { ...wt, classification, classification_label: namedARealAccount ? classification : (wt.classification_label || null), account_id: namedARealAccount ? null : wt.account_id, aiClassification: classification, tax, confidence, canonical_code: result?.canonical_code || wt.canonical_code, canonical_label: result?.canonical_code ? classification : wt.canonical_label, classification_method: result?.classification_method || wt.classification_method, cleaned_description: result?.cleaned_description || wt.description, source_evidence: result?.source_evidence || wt.source_evidence || null, aiClassifying: false } : wt
				);
			} catch {
				workingTransactions = workingTransactions.map(wt =>
					isTheSameQuestion(wt) && wt.aiClassifying ? { ...wt, aiClassifying: false } : wt
				);
			}

			onTransactionsUpdate([...workingTransactions]);
			countOneQuestionAnswered();
		};

		this.modelUnavailableBecause = "";
		this.descriptionsTheModelCouldNotReach = 0;
		await UtilityService.runWithConcurrencyLimit({
			items: needsAi,
			limit: CLASSIFY_CONCURRENCY,
			run: classifyOneDescription,
		});

		if (this.modelUnavailableBecause) {
			const unreachedCount = this.descriptionsTheModelCouldNotReach;
			const everyOneFailed = unreachedCount === needsAi.length;
			const howMany = everyOneFailed
				? `None of the ${needsAi.length} descriptions could be classified`
				: `${unreachedCount} of ${needsAi.length} descriptions could not be classified`;
			alert.error(`${howMany}: ${this.modelUnavailableBecause}. Those transactions are all still here — classify them by hand, or run Classify again once it is sorted.`, { autoClose: false });
		}

		await this.update({ classifyProgress: null, classificationTaxMap: aiTaxMap });

		await this.reconcileNewAccountsAfterClassify(workingTransactions);
		await this.saveTransactionsToServer(workingTransactions);
		if (this.taxesApply()) await this.autoApplyTaxCodes();

		await this.update({ classifyingAi: false, activeTab: 'to-review' });
		UtilityService.alert({ type: "success", text: "Classification complete." });
	};

	onClassify = async (event, options = {}) => {
		try {
			await this.classifyEveryTransactionTheLedgerMissed(event, options);
		} catch (error) {
			await this.releaseTheFileIfSomethingFailed({ because: (error.message || "Try Classify again.") });
			console.error("classification failed:", error);
		}
	};

	collectNewAccounts = (txList) => {
		const transactions = txList || this.getLiveTransactions() || [];
		const inCoa = new Set((this.state.canonicalTaxonomy || []).map(a => a.canonical_code).filter(Boolean));
		const LOW_CONF = new Set(BookkeepingService.listMethodsAPersonMustSignOff());
		const byCode = {};
		for (let t of transactions) {
			const code = t.canonical_code;
			if (!code || inCoa.has(code)) continue;
			if (code.indexOf("SUSPENSE") === 0 || t.classification === "Unclassified") continue;
			const high = (!!t.classification_method && !LOW_CONF.has(t.classification_method)) || !!t.cwClassified;
			if (!byCode[code]) byCode[code] = { code, name: t.canonical_label || t.classification, gifi: t.gifi_code || null, confident: false, txCount: 0 };
			byCode[code].txCount++;
			if (high) byCode[code].confident = true;
		}
		return Object.values(byCode);
	};

	addNewAccountsToCoa = async (accounts) => {
		let existing = this.state.canonicalTaxonomy || [];
		let toAdd = [];
		for (let a of accounts) {
			try {
				let result = await BookkeepingService.createChartOfAccount({
					clientId: this.props.clientId,
					accountName: a.name,
					canonicalCode: a.code,
					gifiCode: a.gifi,
				});
				let resolved = result && (result.account || result.existing);
				if (resolved && !existing.some(x => x.canonical_code === resolved.canonical_code) && !toAdd.some(x => x.canonical_code === resolved.canonical_code)) {
					toAdd.push(resolved);
				}
			} catch (_) {}
		}
		if (toAdd.length > 0) await this.update({ canonicalTaxonomy: [...existing, ...toAdd] });
		return toAdd.length;
	};

	reconcileNewAccountsAfterClassify = async (txList) => {
		const accounts = this.collectNewAccounts(txList).map(a => ({ ...a, checked: a.confident }));
		if (accounts.length === 0) return;
		await this.update({ newAccountReview: accounts });
	};

	toggleNewAccountCheck = (code) => {
		this.update(prev => ({ newAccountReview: (prev.newAccountReview || []).map(a => a.code === code ? { ...a, checked: !a.checked } : a) }));
	};

	dismissNewAccountReview = () => this.update({ newAccountReview: null });

	addReviewedAccountsToCoa = async () => {
		const selected = (this.state.newAccountReview || []).filter(a => a.checked);
		const n = await this.addNewAccountsToCoa(selected);
		await this.update({ newAccountReview: null });
		if (n > 0) UtilityService.alert({ type: "success", text: `Added ${n} ${n === 1 ? "account" : "accounts"} to your chart of accounts.` });
	};

	updateTransactionTaxes({ transactions, classifications }) {
		let taxMap = Object.fromEntries(
			classifications.map(classification => [classification.account, classification.taxRate])
		);
	
		transactions.forEach(transaction => {
			if (taxMap[transaction.classification]) {
				transaction.tax = taxMap[transaction.classification];
			}
		});
	
		return transactions;
	};

	onUpload = async e => {
		await this.processReferenceFiles(Array.from(e.target.files));
	};

	categorizeAgainstTheLedger = async ({ rows, generalLedgerReference, accountTaxes }) => {
		const { clientId } = this.props;

		let categorized;
		try {
			categorized = await BookkeepingService.categorizeTransactionsFromGeneralLedger({ clientId, generalLedgerReference, transactions: rows });
		} catch (error) {
			await this.releaseTheFileIfSomethingFailed({ because: "The general ledger could not be matched: " + (error.message || "no response") });
			return null;
		}

		const rowsWithCategories = BookkeepingService.stampGeneralLedgerCategories({
			transactions: rows,
			categorized,
			accountTaxes,
			clientChart: this.state.canonicalTaxonomy || [],
			canonicalTaxonomyOptions: this.state.canonicalTaxonomyOptions || [],
		});
		return rowsWithCategories;
	};

	settleRowsWithoutAnAccount = async () => {
		const { transactions, onTransactionsUpdate } = this.props;
		const ledgerEntries = this.state.generalLedgerEntries || [];
		if (ledgerEntries.length === 0 || this.rowsWithoutAnAccountBeingSettled) return false;

		const rowsNeedingAnAccount = new Set(BookkeepingService.getTransactionsNeedingClassification({ transactions }));
		if (rowsNeedingAnAccount.size === 0) return false;

		this.rowsWithoutAnAccountBeingSettled = true;
		this.generalLedgerPassRunning = true;
		await this.update({ settlingTheFile: "ledger" });

		const indexesNeedingAnAccount = transactions.map((transaction, index) => (rowsNeedingAnAccount.has(transaction) ? index : -1)).filter(index => index >= 0);
		const rows = indexesNeedingAnAccount.map(index => transactions[index]);
		const { generalLedgerReference } = BookkeepingService.buildGeneralLedgerReferenceFromEntries({ entries: ledgerEntries });
		const rowsWithCategories = await this.categorizeAgainstTheLedger({ rows, generalLedgerReference, accountTaxes: {} });

		this.generalLedgerPassRunning = false;
		this.rowsWithoutAnAccountBeingSettled = false;
		if (!rowsWithCategories) return true;

		const settledTransactions = [...transactions];
		indexesNeedingAnAccount.forEach((index, position) => { settledTransactions[index] = rowsWithCategories[position]; });

		onTransactionsUpdate(settledTransactions);
		await this.saveTransactionsToServer(settledTransactions);
		await this.update({ settlingTheFile: null });

		if (this.canStartAutomaticClassification({ knownTransactions: settledTransactions })) {
			await this.startAutomaticClassification({ knownTransactions: settledTransactions });
			return true;
		}
		this.autoApplyTaxCodes();
		return true;
	};

	applyGeneralLedgerReference = async ({ generalLedgerReference, generalLedgerEntries, accountList, accountTaxes }) => {
		const { transactions, onTransactionsUpdate } = this.props;

		await this.update({ loading: true, settlingTheFile: "ledger" });

		const transactionsWithCategories = await this.categorizeAgainstTheLedger({ rows: transactions, generalLedgerReference, accountTaxes });
		if (!transactionsWithCategories) return transactions;

		const existingNames = new Set(this.state.classifications.map(c => c.name));
		const newClassifications = (accountList || []).filter(name => !existingNames.has(name)).map(name => ({ name }));

		await this.update({
			classifications: [...this.state.classifications, ...newClassifications],
			classificationTaxMap: { ...this.state.classificationTaxMap, ...(accountTaxes || {}) },
			generalLedgerReferences: generalLedgerReference,
			generalLedgerEntries: generalLedgerEntries || [],
			loading: false,
		});
		onTransactionsUpdate(transactionsWithCategories);
		await this.saveTransactionsToServer(transactionsWithCategories, { persistGlEntries: true });
		this.autoApplyTaxCodes();
		await this.update({ settlingTheFile: null });
		return transactionsWithCategories;
	};

	processReferenceFiles = async files => {
		let { clientId, transactions, onTransactionsUpdate } = this.props;

		await this.update({ loading: true });

		try {
			const bookkeepingFiles = files.filter(f =>
				["am.dbf", "bl.dbf", "gl.dbf"].some(s => f.name.toLowerCase().endsWith(s))
			);
			const response = await BookkeepingService.uploadCasewareReference({
				clientId,
				files: bookkeepingFiles,
				transactions,
				wpfEngagementId: this.props.wpfEngagementId,
			});

			let { fullTrialBalanceData, casewareCategorizedAccounts, extractedGeneralLedgerMapping, glQuality, glTaxRatesByAccount } = response?.data ? response.data : response;

			const casewareAccountTaxes = BookkeepingService.buildAccountTaxCodesFromGeneralLedgerRates({
				ratesByAccountNumber: glTaxRatesByAccount,
				accounts: fullTrialBalanceData,
			});
			if (Object.keys(casewareAccountTaxes).length > 0) {
				await this.update({ classificationTaxMap: { ...this.state.classificationTaxMap, ...casewareAccountTaxes } });
			}

			const priorYearTrialBalanceExtract = fullTrialBalanceData.map(account => ({
				accountNumber: account.accountNumber,
				accountType: account.accountType,
				accountClass: account.accountClass,
				accountDescription: account.accountDescription,
				priorYearAmount: account.priorYearAmount
			}));

			const uploadedClassifications = fullTrialBalanceData
				.filter(account => account.accountNumber && account.accountDescription)
				.map(account => ({
					name: `${account.accountNumber} - ${account.accountDescription}`,
					accountType: account.accountType,
					accountClass: account.accountClass,
				}));

			let accountsWithDescriptions = BookkeepingService.mapAccountDescriptionToAccount({
				casewareCategorizedAccounts,
				priorYearTrialBalanceExtract
			});

			let transactionsWithCategories = transactions.map((transaction, index) => {
				let classification = accountsWithDescriptions[index];
				return { ...transaction, classification, cwClassification: classification || null, cwClassified: !!(classification && classification !== "Unclassified") };
			});

			const { industry, province } = this.state;
			const importedClassifications = uploadedClassifications.length > 0 ? uploadedClassifications : this.state.classifications;
			const classificationsToUse = BookkeepingService.ensureSuspenseClassification({ classifications: importedClassifications });

			await this.update({
				classifications: classificationsToUse,
				priorYearTrialBalance: priorYearTrialBalanceExtract,
				loading: false,
				generalLedgerReferences: extractedGeneralLedgerMapping || {},
				glQuality: glQuality || null,
			});

			onTransactionsUpdate(transactionsWithCategories);

			this.autoApplyTaxCodes();

			let cwClassifiedCount = transactionsWithCategories.filter(function (t) { return t.cwClassified; }).length;
			let hasUsefulGL = glQuality && glQuality.nonAdjustingEntries > 0;
			if (!hasUsefulGL && cwClassifiedCount === 0 && transactionsWithCategories.length > 0 && industry && province) {
				setTimeout(() => this.onClassify(), 500);
			}

		} catch (error) {
			console.error("Upload failed:", error);
			await this.update({
				loading: false
			});
		}
	};

	toggleClassificationExpand = (index) => {
		const expanded = new Set(this.state.expandedClassificationRows);
		if (expanded.has(index)) expanded.delete(index); else expanded.add(index);
		this.update({ expandedClassificationRows: expanded });
	};

	componentWillUnmount() {
		this.filterOnWhatWasTyped.cancel();
	}

	returnTheWindowToTheTopWhenTheListChanges = ({ prevState }) => {
		if (!prevState) return;

		const theListItSelfChanged =
			prevState.search !== this.state.search ||
			prevState.activeTab !== this.state.activeTab ||
			prevState.bankFilter !== this.state.bankFilter;
		if (!theListItSelfChanged) return;

		if (this.state.firstRowOnScreen !== 0) this.update({ firstRowOnScreen: 0 });
		if (this.scrollAreaRef.current) this.scrollAreaRef.current.scrollTop = 0;
	};

	onTableScrolled = () => {
		const scroller = this.scrollAreaRef.current;
		if (!scroller) return;

		const firstRowOnScreen = Math.max(0, Math.floor(scroller.scrollTop / ROW_HEIGHT));
		const rowsThatFitOnScreen = Math.ceil(scroller.clientHeight / ROW_HEIGHT);

		const alreadyShowingTheseRows = firstRowOnScreen === this.state.firstRowOnScreen && rowsThatFitOnScreen === this.state.rowsThatFitOnScreen;
		if (alreadyShowingTheseRows) return;

		const stillOnScreen = this.readRowsWorthRendering({ firstRowOnScreen, rowsThatFitOnScreen });
		const expandedClassificationRows = new Set(
			[...this.state.expandedClassificationRows].filter(index => index >= stillOnScreen.from && index < stillOnScreen.to)
		);

		this.update({ firstRowOnScreen, rowsThatFitOnScreen, expandedClassificationRows });
	};

	readRowsWorthRendering = ({ firstRowOnScreen, rowsThatFitOnScreen }) => {
		const from = Math.max(0, firstRowOnScreen - ROWS_KEPT_BEYOND_THE_EDGES);
		const to = firstRowOnScreen + rowsThatFitOnScreen + ROWS_KEPT_BEYOND_THE_EDGES;
		return { from, to };
	};

	onSearchTyped = event => {
		this.filterOnWhatWasTyped(event.target.value);
	};

	filterOnWhatWasTyped = debounce(typed => {
		this.update({ search: typed });
	}, 200);

	getStatementTypeByCanonicalCode = () => {
		const statementTypeByCanonicalCode = {};
		for (const node of (this.state.canonicalTaxonomyOptions || [])) statementTypeByCanonicalCode[node.canonical_code] = node.statement_type;
		return statementTypeByCanonicalCode;
	};

	getSortedClassifications = () => {
		const { transactions } = this.props;
		const classificationMap = {};

		for (let t of (transactions || [])) {
			if (t.classification && t.classification !== "Unclassified" && t.classification !== "uncategorized" && !classificationMap[t.classification]) {
				classificationMap[t.classification] = {
					name: t.classification,
					canonical_code: t.canonical_code || null,
					gifi_code: t.gifi_code || null,
					canonical_label: t.canonical_label || t.classification,
				};
			}
		}

		for (let c of (this.state.canonicalTaxonomy || [])) {
			let name = BookkeepingService.describeChartAccount({ account: c }) || c.display_name;
			if (name && !classificationMap[name]) {
				classificationMap[name] = {
					name,
					canonical_code: c.canonical_code,
					gifi_code: c.gifi_code,
					canonical_label: name,
				};
			}
		}

		for (let c of this.state.classifications) {
			if (c.name && !classificationMap[c.name]) {
				classificationMap[c.name] = { name: c.name, canonical_code: c.canonical_code || null, gifi_code: c.gifi_code || null, canonical_label: c.canonical_label || c.name };
			}
		}

		const statementTypeByCode = this.getStatementTypeByCanonicalCode();

		const named = Object.values(classificationMap).map(classification => {
			const code = classification.canonical_code || this.getCanonicalCodeForAccount(classification.name);
			return { ...classification, statement_type: code ? statementTypeByCode[code] || null : null };
		});

		return named.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
	};

	taxesApply = () => this.state.hasTax || !!this.props.gstHstAccount;

	onHasTaxToggle = async () => {
		const taxesTurnedOn = !this.state.hasTax;
		await this.update({ hasTax: taxesTurnedOn });
		if (!taxesTurnedOn) return;

		const provinceMissing = !this.state.province;
		const noTaxCodesAssigned = !(this.getLiveTransactions() || []).some(t => t.tax);
		if (provinceMissing || noTaxCodesAssigned) {
			await this.openTaxSettings();
		}
	};

	calculateTaxAmount = ({ amount, tax }) => {
		const taxesApply = this.taxesApply();

		if (!taxesApply) return 0.0;

		let taxAmount = BookkeepingService.calculateTaxAmount({ amount, tax, hasTax: taxesApply });

		return taxAmount;
	};

	buildForeignCurrencyDetail = ({ transaction }) => {
		const rate = transaction.exchange_rate != null ? Number(transaction.exchange_rate) : null;
		const rateDate = transaction.exchangeRateDate || transaction.date;
		const cadAmount = Number(transaction.amount) || 0;
		const originalAmount = transaction.originalAmount != null ? Number(transaction.originalAmount) : (rate ? cadAmount / rate : null);
		const originalText = originalAmount != null ? originalAmount.toFixed(2) : "?";
		const rateText = rate != null ? rate.toFixed(4) : "?";
		const detail = `${transaction.currency} ${originalText} × ${rateText} (Bank of Canada rate on ${rateDate}) = CAD ${cadAmount.toFixed(2)}`;
		return detail;
	};

	renderCurrencyCell = ({ transaction }) => {
		const currency = transaction.currency || "CAD";
		const isForeignCurrency = currency !== "CAD";
		return (
			<div
				className={clsx("t__row__item", "bk-table__col--currency", isForeignCurrency && "bk-table__col--currency--fx")}
				title={isForeignCurrency ? this.buildForeignCurrencyDetail({ transaction }) : undefined}
			>
				{currency}
			</div>
		);
	};

	sortBy = async field => {
		let { sortOrder } = this.state;

		await this.update({
			sortField: field,
			sortOrder: sortOrder === "asc" ? "desc" : "asc"
		});

		this.sortTransactions();
	};

	sortTransactions = () => {
		const { sortField, sortOrder } = this.state;
		const { transactions, onTransactionsUpdate } = this.props;

		if (!sortField) return;

		const sortedTransactions = [...transactions].sort((a, b) => {
			if (a[sortField] < b[sortField]) return sortOrder === "asc" ? -1 : 1;
			if (a[sortField] > b[sortField]) return sortOrder === "asc" ? 1 : -1;
			return 0;
		});

		onTransactionsUpdate(sortedTransactions);
	};

	renderConfidence = (confidence, cwClassified, glClassified) => {
		if (cwClassified && (confidence === null || confidence === undefined)) {
			return <span className="bk-table__badge bk-table__badge--match">{glClassified ? "GL Match" : "CW Auto"}</span>;
		}
		if (confidence === null || confidence === undefined) return <span className="bk-table__confidence--empty">{"—"}</span>;
		const pct = Math.round(confidence * 100);
		const level = pct >= 80 ? "high" : pct >= 50 ? "mid" : "low";
		return <span className={`bk-table__badge bk-table__badge--${level}`}>{pct}%</span>;
	};

	renderTaxValue = tax => {
		if (!tax) return "";
		const hasDigit = [...tax].some(character => character >= "0" && character <= "9");
		if (hasDigit) return tax;
		return <span className="bk-table__tax-badge">{tax}</span>;
	};

	getClassificationDotType = transaction => {
		const isUnclassified = !transaction.classification || transaction.classification === "Unclassified" || transaction.classification === "uncategorized";
		if (isUnclassified) return "unclassified";

		const canonicalCode = transaction.canonical_code || "";
		const topSegment = canonicalCode.split(".")[0].toUpperCase();
		if (topSegment === "REVENUE" || topSegment === "INCOME") return "income";
		if (topSegment === "EXPENSE" || topSegment === "COGS") return "expense";
		if (topSegment === "ASSET") return "asset";
		if (topSegment === "LIABILITY") return "liability";
		if (topSegment === "EQUITY") return "equity";
		return "other";
	};

	renderSortIcon = field => {
		let { sortField, sortOrder } = this.state;
		const activeAsc = sortField === field && sortOrder === "asc";
		const activeDesc = sortField === field && sortOrder === "desc";

		return (
			<span className="bk-table__sort-icon">
				<Icon.ChevronUp size={11} className={clsx("bk-table__sort-arrow", activeAsc && "bk-table__sort-arrow--active")} />
				<Icon.ChevronDown size={11} className={clsx("bk-table__sort-arrow", activeDesc && "bk-table__sort-arrow--active")} />
			</span>
		);
	};

	renderCanonicalStatusBar = () => {
		let { transactions } = this.props;
		let txList = transactions || [];

		let unresolvedCount = txList.filter(tx => tx.canonical_code && tx.canonical_code.startsWith("SUSPENSE.")).length;
		let overrideCount = txList.filter(tx => tx.suspenseAccepted).length;

		if (unresolvedCount === 0 && overrideCount === 0) return null;

		return (
			<div className="bk-table__canonical-status-bar">
				{unresolvedCount > 0 && (
					<span className="bk-table__canonical-status-bar__unresolved">
						{unresolvedCount} {unresolvedCount === 1 ? "transaction has" : "transactions have"} no FS Account mapping — reclassify {unresolvedCount === 1 ? "it" : "them"} above, or {unresolvedCount === 1 ? "it" : "they"} will be documented as {unresolvedCount === 1 ? "an exception" : "exceptions"} in your AFS.
					</span>
				)}
				{overrideCount > 0 && (
					<span className="bk-table__canonical-status-bar__overrides">
						{overrideCount} manual {overrideCount === 1 ? "override" : "overrides"}
					</span>
				)}
			</div>
		);
	};

	getTransferPairs = () => {
		const rowsThisWasWorkedOutFrom = this.props.transactions;
		const cache = this._transferPairsCache;
		if (cache && cache.rowsThisWasWorkedOutFrom === rowsThisWasWorkedOutFrom) return cache.pairs;

		const pairs = BookkeepingService.getTransferPairs({ transactions: this.getLiveTransactions() || [] });
		this._transferPairsCache = { rowsThisWasWorkedOutFrom, pairs };
		return pairs;
	};

	getRowsStillNeedingAnAccount = ({ transactions }) => {
		const settledAsTransfers = this.getRowsAlreadySettledAsTransfers();
		return BookkeepingService.listTransactionsStillNeedingAnAccount({ transactions, settledAsTransfers });
	};

	getRowsAlreadySettledAsTransfers = () => {
		const pairs = this.getTransferPairs();
		return BookkeepingService.listTransactionsAlreadySettledAsTransfers({ pairs });
	};

	transferPairKey = ({ pair }) => {
		const outAccount = pair.outLeg.accountName || pair.outLeg.bankAccountName || "";
		const inAccount = pair.inLeg.accountName || pair.inLeg.bankAccountName || "";
		return `${pair.outLeg.date}:${pair.outLeg.amount}:${outAccount}|${pair.inLeg.date}:${pair.inLeg.amount}:${inAccount}`;
	};

	onToggleTransferSelected = ({ pair }) => {
		const key = this.transferPairKey({ pair });
		const selectedTransferKeys = { ...this.state.selectedTransferKeys };
		if (selectedTransferKeys[key]) {
			delete selectedTransferKeys[key];
		} else {
			selectedTransferKeys[key] = true;
		}
		this.update({ selectedTransferKeys });
	};

	onToggleSelectAllTransfers = ({ pairs }) => {
		const everyPairSelected = pairs.length > 0 && pairs.every(pair => this.state.selectedTransferKeys[this.transferPairKey({ pair })]);
		if (everyPairSelected) {
			this.update({ selectedTransferKeys: {} });
			return;
		}

		const selectedTransferKeys = {};
		for (const pair of pairs) {
			selectedTransferKeys[this.transferPairKey({ pair })] = true;
		}
		this.update({ selectedTransferKeys });
	};

	findChartAccountForBankName = ({ bankName }) => {
		const link = (this.state.bankAccountLinks || []).find(candidate => candidate.accountName === bankName);
		if (link && link.postsTo) return link.postsTo;
		return bankName || "an untied account";
	};

	reloadTransactionsFromServer = async () => {
		const { clientId, wpfEngagementId, onTransactionsUpdate } = this.props;
		const loaded = await BookkeepingService.loadEngagementTransactions({ clientId, wpfEngagementId });
		if (loaded && Array.isArray(loaded.transactions) && loaded.transactions.length > 0) {
			const fromTheServer = BookkeepingService.mapEngagementTransactionsForTable({ transactions: loaded.transactions });
			this.serverHasTransactions = BookkeepingService.rememberWhatTheServerHas({ transactions: fromTheServer });
			await onTransactionsUpdate(this.keepingWhatThePreparerHadTicked({ fromTheServer }));
		}
	};

	keepingWhatThePreparerHadTicked = ({ fromTheServer }) => {
		const ticked = new Set((this.props.transactions || []).filter(transaction => transaction.selected).map(transaction => transaction.id));
		if (ticked.size === 0) return fromTheServer;

		return fromTheServer.map(transaction => (ticked.has(transaction.id) ? { ...transaction, selected: true } : transaction));
	};

	countOneTransferFinished = () => {
		const working = this.state.transfersWorking;
		if (!working) return;
		this.update({ transfersWorking: { ...working, done: working.done + 1 } });
	};

	onApproveTransfers = async ({ pairs }) => {
		if (pairs.length === 0 || this.state.transfersWorking) return;
		await this.update({ selectedTransferKeys: {}, transfersWorking: { verb: "Approving", total: pairs.length } });

		const unsaved = pairs.filter(pair => !BookkeepingService.checkTransferPairIsRecordable({ pair }).isRecordable);
		const readyToRecord = pairs.filter(pair => BookkeepingService.checkTransferPairIsRecordable({ pair }).isRecordable);

		const answer = readyToRecord.length > 0
			? await BookkeepingService.recordTransferPairs({
				pairs: readyToRecord.map(pair => ({ outgoingTransactionId: pair.outLeg.id, incomingTransactionId: pair.inLeg.id })),
			})
			: { results: [] };

		const results = (answer && answer.results) || [];
		const recorded = results.filter(result => result.paired).length;
		const refused = results.filter(result => !result.paired).map(result => result.reason || "That pair could not be recorded.");
		const disagreements = results.filter(result => result.paired && result.disagreement).map(result => result.disagreement);
		for (const pair of unsaved) refused.push(BookkeepingService.checkTransferPairIsRecordable({ pair }).error);

		if (recorded > 0) alert.success(recorded + " transfer" + (recorded > 1 ? "s" : "") + " recorded — each payment now counts once.");
		for (const reason of [...new Set(refused)]) alert.error(reason);
		for (const disagreement of disagreements) {
			alert.info("The books post " + disagreement.description + " to " + disagreement.ledgerSays + ", but the statements show it arriving in " + disagreement.statementsSay + ".");
		}

		await this.reloadTransactionsFromServer();
		await this.update({ transfersWorking: null });
	};

	onReverseTransfers = async ({ pairs }) => {
		if (pairs.length === 0 || this.state.transfersWorking) return;
		await this.update({ selectedTransferKeys: {}, transfersWorking: { verb: "Reversing", done: 0, total: pairs.length } });

		const results = await Promise.all(pairs.map(async pair => {
			if (!pair.inLeg || !pair.inLeg.id) {
				this.countOneTransferFinished();
				return null;
			}
			const result = await BookkeepingService.releaseTransferPair({ transactionId: pair.inLeg.id });
			this.countOneTransferFinished();
			return result;
		}));

		const released = results.filter(result => result && result.released).length;
		if (released > 0) alert.success(released + " transfer" + (released > 1 ? "s" : "") + " undone — both legs post again.");

		await this.reloadTransactionsFromServer();
		await this.update({ transfersWorking: null });
	};

	onRejectTransfers = async ({ pairs }) => {
		if (pairs.length === 0 || this.state.transfersWorking) return;
		const { transactions, onTransactionsUpdate } = this.props;
		const updatedTransactions = BookkeepingService.rejectTransferPairs({ transactions, pairs });
		await this.update({ selectedTransferKeys: {}, transfersWorking: { verb: "Rejecting", done: 0, total: pairs.length } });
		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
		await this.update({ transfersWorking: null });
	};

	onMarkTransactionAsTransfer = transaction => {
		this.update({ classifyModalTransaction: null, manualTransferModalOpen: true, manualTransferSeed: [transaction] });
	};

	onMarkAsTransferFromReview = transaction => {
		this.closeAllocationReview();
		this.onMarkTransactionAsTransfer(transaction);
	};

	closeManualTransfer = () => this.update({ manualTransferModalOpen: false, manualTransferSeed: [] });

	onManualTransferConfirm = async ({ transactionA, transactionB }) => {
		const { pair, error } = BookkeepingService.buildManualTransferPair({ transactionA, transactionB });
		if (error) {
			alert.error(error);
			return;
		}

		await this.update({ manualTransferModalOpen: false, manualTransferSeed: [] });
		await this.onApproveTransfers({ pairs: [pair] });
	};

	renderManualTransferModal = () => {
		if (!this.state.manualTransferModalOpen) return null;
		return (
			<ManualTransferModal
				transactions={this.getLiveTransactions()}
				seedTransactions={this.state.manualTransferSeed}
				onConfirm={this.onManualTransferConfirm}
				onClose={this.closeManualTransfer}
			/>
		);
	};

	renderTransferLeg = ({ leg, direction, note }) => {
		const accountName = leg.accountName || leg.bankAccountName || "";
		const currencyCode = leg.currency || "CAD";
		const isForeignCurrency = currencyCode !== "CAD";
		const nativeAmount = isForeignCurrency && leg.originalAmount != null ? Number(leg.originalAmount) : Number(leg.amount);
		const nativeAmountText = UtilityService.formatCurrency(nativeAmount);
		const cadEquivalentText = UtilityService.formatCurrency(Number(leg.amount) || 0);
		const isOut = direction === "out";
		const label = isOut ? "FROM" : "TO";
		const amountModifier = isOut ? "bk-transfers__leg-amount--out" : "bk-transfers__leg-amount--in";
		const iconModifier = isOut ? "bk-transfers__leg-icon--out" : "bk-transfers__leg-icon--in";
		const icon = <span className={clsx("bk-transfers__leg-icon", iconModifier)}><Landmark size={15} /></span>;
		const info = (
			<div className="bk-transfers__leg-info">
				<div className="bk-transfers__leg-account">{accountName}</div>
				<div className="bk-transfers__leg-detail">{leg.date} · {leg.description}</div>
				<div className={clsx("bk-transfers__leg-amount", amountModifier)}>{nativeAmountText} {currencyCode}</div>
				{isForeignCurrency && <div className="bk-transfers__leg-cad">≈ CAD {cadEquivalentText}</div>}
				{note}
			</div>
		);
		return (
			<div className="bk-transfers__leg">
				<div className="bk-transfers__leg-label">{label}</div>
				<div className="bk-transfers__leg-body">
					{icon}
					{info}
				</div>
			</div>
		);
	};

	renderTransferPairRow = pair => {
		const key = this.transferPairKey({ pair });
		const isSelected = !!this.state.selectedTransferKeys[key];
		const isProposed = pair.status === "proposed";
		const isWorking = !!this.state.transfersWorking;
		const outCadAmount = Number(pair.outLeg.amount) || 0;
		const inCadAmount = Number(pair.inLeg.amount) || 0;
		const fxDifferenceCad = Math.abs(outCadAmount + inCadAmount);
		const hasFxDifference = pair.isCrossCurrency && fxDifferenceCad >= 0.01;

		const inLegNote = hasFxDifference ? (
			<div className="bk-transfers__fxdiff"><Icon.AlertTriangle size={12} /> FX difference: CAD {UtilityService.formatCurrency(fxDifferenceCad)}</div>
		) : null;

		const paidFrom = this.findChartAccountForBankName({ bankName: pair.outLeg.accountName });
		const paidTo = this.findChartAccountForBankName({ bankName: pair.inLeg.accountName });
		const approvalEffect = pair.isCrossCurrency
			? `These accounts are in different currencies, so this cannot be approved here yet — the exchange difference has to be recognised as a foreign exchange gain or loss. Record it by hand for now.`
			: `On approve: recorded once — ${paidFrom} is credited and ${paidTo} is debited. The matching line stops posting so the payment is not counted twice.`;

		return (
			<div key={key} className={clsx("bk-transfers__row", isSelected && "bk-transfers__row--selected")}>
				<span className="bk-transfers__check" onClick={event => event.stopPropagation()}>
					<Checkbox isSmall checked={isSelected} onChange={() => this.onToggleTransferSelected({ pair })} />
				</span>
				<div className="bk-transfers__pair">
					{this.renderTransferLeg({ leg: pair.outLeg, direction: "out" })}
					<span className="bk-transfers__connector-icon"><Icon.ArrowRight size={13} /></span>
					{this.renderTransferLeg({ leg: pair.inLeg, direction: "in", note: inLegNote })}
					{isProposed && <div className="bk-transfers__effect">{approvalEffect}</div>}
				</div>
				<div className="bk-transfers__side">
					{isProposed ? (
						<>
							<button type="button" className="tk-button tk-button--secondary tk-button--compact" disabled={isWorking} onClick={() => this.onApproveTransfers({ pairs: [pair] })}>Approve</button>
					<button type="button" className="tk-text-action tk-text-action--muted" disabled={isWorking} onClick={() => this.onRejectTransfers({ pairs: [pair] })}>Reject</button>
						</>
					) : (
						<button type="button" className="tk-button tk-button--secondary tk-button--compact" disabled={isWorking} onClick={() => this.onReverseTransfers({ pairs: [pair] })}>
					<Icon.RotateCcw size={14} />
					Reverse
				</button>
					)}
				</div>
			</div>
		);
	};

	renderTransfersWorking = ({ working }) => {
		const countsAsItGoes = working.done !== undefined;
		const plural = working.total === 1 ? "transfer" : "transfers";
		const label = countsAsItGoes
			? `${working.verb} ${Math.min(working.done, working.total)} of ${working.total}…`
			: `${working.verb} ${working.total} ${plural}…`;
		return (
			<span className="bk-transfers__working" aria-live="polite">
				<Icon.Loader size={14} className="bk-transfers__working-spinner" />
				{label}
			</span>
		);
	};

	renderTransfersView = () => {
		const pairs = this.getTransferPairs();
		if (pairs.length === 0) {
			return <div className="bk-transfers__empty">No transfers detected between the client's accounts.</div>;
		}

		const proposedPairs = pairs.filter(pair => pair.status === "proposed");
		const selectedPairs = pairs.filter(pair => this.state.selectedTransferKeys[this.transferPairKey({ pair })]);
		const selectedProposed = selectedPairs.filter(pair => pair.status === "proposed");
		const selectedApplied = selectedPairs.filter(pair => pair.status === "applied");
		const everyPairSelected = pairs.length > 0 && selectedPairs.length === pairs.length;

		const hasSelection = selectedPairs.length > 0;
		const working = this.state.transfersWorking;
		const isWorking = !!working;
		return (
			<div className="bk-transfers">
				<div className="bk-transfers__intro">
					<Icon.Repeat size={15} className="bk-transfers__intro-icon" />
					<span>Transfers move money between the client's own accounts, so the same payment appears on both statements. Approving records it once — against the two accounts named on the row — and stops the second line posting. Which accounts those are follows from where the money actually moved; correct a wrong one under Settings → Bank Accounts.</span>
				</div>
				<div className="bk-transfers__bar">
					<Checkbox isSmall title={`Select all (${pairs.length})`} checked={everyPairSelected} onChange={() => this.onToggleSelectAllTransfers({ pairs })} />
					<div className="bk-transfers__bar-actions">
						{isWorking ? (
							this.renderTransfersWorking({ working })
						) : hasSelection ? (
							<>
								{selectedProposed.length > 0 && (
									<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={() => this.onApproveTransfers({ pairs: selectedProposed })}>
										<Icon.Check size={14} />
										{`Approve selected (${selectedProposed.length})`}
									</button>
								)}
								{selectedProposed.length > 0 && (
									<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onRejectTransfers({ pairs: selectedProposed })}>
										<Icon.X size={14} />
										{`Reject selected (${selectedProposed.length})`}
									</button>
								)}
								{selectedApplied.length > 0 && (
									<button type="button" className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onReverseTransfers({ pairs: selectedApplied })}>
										<Icon.RotateCcw size={14} />
										{`Reverse selected (${selectedApplied.length})`}
									</button>
								)}
							</>
						) : (
							proposedPairs.length > 0 && (
								<button type="button" className="tk-button tk-button--primary tk-button--compact" onClick={() => this.onApproveTransfers({ pairs: proposedPairs })}>
									<Icon.CheckCircle size={14} />
									{`Approve all ${proposedPairs.length}`}
								</button>
							)
						)}
					</div>
				</div>
				<div className="bk-transfers__list">
					{pairs.map(this.renderTransferPairRow)}
				</div>
				<div className="bk-transfers__footer">Showing {pairs.length} of {pairs.length} transfers</div>
			</div>
		);
	};

	releaseTheFileIfSomethingFailed = async ({ because }) => {
		if (!this.theToolIsSettlingTheFile()) return;
		await this.update({ classifyingAi: false, classifyProgress: null, settlingTheFile: null, loading: false });
		alert.error("The automatic pass stopped before it finished, so the table has been unlocked. " + because);
	};

	theToolIsSettlingTheFile = () => {
		const { classifyingAi, settlingTheFile } = this.state;
		return !!classifyingAi || !!settlingTheFile;
	};

	renderClassifyingOverlay = () => {
		const { classifyingAi, classifyProgress } = this.state;
		if (!this.theToolIsSettlingTheFile()) return null;

		const done = classifyProgress ? classifyProgress.done : 0;
		const total = classifyProgress ? classifyProgress.total : 0;
		const headline = classifyingAi && total > 0 ? `${done} of ${total} answered` : "Working through the file…";
		const note = classifyingAi
			? "Classifying the transactions the general ledger did not cover. Each merchant is looked at once, so this takes a moment."
			: "Matching the transactions against the general ledger you imported.";

		return (
			<div className="bk-table__busy" aria-live="polite" aria-busy="true">
				<div className="bk-table__busy-panel">
					<Loader className="bk-table__busy-loader" />
					<div className="bk-table__busy-count">{headline}</div>
					<div className="bk-table__busy-note">
						{note} The table is locked until it finishes, so nothing you change is overwritten.
					</div>
				</div>
			</div>
		);
	};

	renderTabs = () => {
		const { activeTab } = this.state;
		const { transactions } = this.props;
		const everyRow = transactions || [];
		const archivedCount = everyRow.filter(t => t.isArchived).length;
		const txList = everyRow.filter(t => !t.isArchived);
		const rowsSettledAsTransfers = this.getRowsAlreadySettledAsTransfers();
		const unresolvedCount = txList.filter(t =>
			!rowsSettledAsTransfers.has(t) && (
				(t.canonical_code && t.canonical_code.startsWith('SUSPENSE.') && !t.suspenseAccepted) ||
				(!t.canonical_code && t.classification && t.classification !== 'Unclassified' && t.classification !== 'uncategorized')
			)
		).length;
		const splitDescriptionCount = BookkeepingService.findDescriptionsAnsweredMoreThanOneWay({ transactions: txList }).count;

		const counts = {
			archived: archivedCount,
			all: txList.length,
			'to-review': txList.filter(t => BookkeepingService.awaitsReviewerSignOff({ transaction: t })).length,
			'auto-classified': txList.filter(t => t.glClassified || t.cwClassified).length,
			approved: txList.filter(t => t.classification_status === 'approved').length,
			unclassified: this.getRowsStillNeedingAnAccount({ transactions: txList }).length,
			transfers: this.getTransferPairs().length,
		};

		const tabs = [
			{ key: 'transfers', label: 'Transfers', title: 'Money moved between the client’s own accounts. Confident same-currency transfers are applied automatically; review and approve the rest, or reverse any.' },
			{ key: 'to-review', label: 'To Review', title: 'Classifications the AI chose that need your sign-off. Accepting one moves it to Approved; it does not create a rule.' },
			{ key: 'auto-classified', label: 'From the GL', title: 'Classified from the general ledger you imported. These match an account the client already posts this description to.' },
			{ key: 'approved', label: 'Approved', title: 'Classifications you or a previous engagement have accepted.' },
			{ key: 'unclassified', label: 'Unclassified', title: 'Transactions with no classification yet. Select a row to classify manually.' },
			{ key: 'all', label: 'All', title: 'All transactions.' },
			{ key: 'archived', label: 'Archived', title: 'Transactions removed from these books. They are kept with the reason they were removed and can be restored at any time.' },
		];

		return (
			<div className="bk-tabs">
				{tabs.map(tab => (
					<button
						key={tab.key}
						className={"bk-tabs__tab" + (activeTab === tab.key ? " bk-tabs__tab--active" : "") + (counts[tab.key] === 0 && tab.key !== 'all' ? " bk-tabs__tab--empty" : "") + (tab.key === 'to-review' && counts['to-review'] > 0 ? " bk-tabs__tab--attention" : "")}
						onClick={counts[tab.key] === 0 && tab.key !== 'all' ? null : () => this.update({ activeTab: tab.key })}
						title={tab.title}
					>
						{tab.label}
						<span className={clsx("bk-tabs__count", counts[tab.key] === 0 ? "bk-tabs__count--zero" : `bk-tabs__count--${tab.key}`)}>{counts[tab.key]}</span>
					</button>
				))}
			{unresolvedCount > 0 && (
				<ToolsTooltip
					content="Click to filter transactions with no FS Account mapping."
					className={"bk-tabs__notice bk-tabs__notice--clickable" + (activeTab === "suspense" ? " bk-tabs__notice--active" : "")}
					onClick={() => this.update({ activeTab: activeTab === "suspense" ? "all" : "suspense" })}
				>
					<Icon.AlertTriangle size={12} />
					{unresolvedCount} with no FS Account
				</ToolsTooltip>
			)}
			{splitDescriptionCount > 0 && (
				<ToolsTooltip
					content="The same description was answered more than one way — different accounts, or classified on some rows and left unclassified on others. Click to review them and settle it."
					className={"bk-tabs__notice bk-tabs__notice--clickable" + (activeTab === "inconsistent" ? " bk-tabs__notice--active" : "")}
					onClick={() => this.update({ activeTab: activeTab === "inconsistent" ? "all" : "inconsistent" })}
				>
					<Icon.AlertTriangle size={12} />
					{splitDescriptionCount} answered inconsistently
				</ToolsTooltip>
			)}
			</div>
		);
	};

	renderNextStepBanner = () => {
		const { classifyingAi, compilationData, afsActive, afsLoading } = this.state;
		const { transactions } = this.props;
		const txList = transactions || [];

		const totalTransactions = txList.length;
		if (totalTransactions === 0) return null;

		const toReviewCount = txList.filter(t => BookkeepingService.awaitsReviewerSignOff({ transaction: t })).length;
		const suspenseCount = txList.filter(t => t.canonical_code && t.canonical_code.startsWith("SUSPENSE.") && !t.suspenseAccepted).length;
		const stillNeedingAnAccount = this.getRowsStillNeedingAnAccount({ transactions: txList }).length;
		const isClassified = stillNeedingAnAccount === 0;
		const hasCompilation = compilationData && Object.keys(compilationData).length > 0;

		let icon = null;
		let message = null;
		let action = null;
		let variant = "info";

		if (afsLoading) {
			icon = <Icon.Loader size={14} />;
			message = "Generating your financial statements...";
		} else if (classifyingAi) {
			icon = <Icon.Loader size={14} />;
			message = "Classifying the transactions the general ledger did not cover. Each one is looked at individually, so this takes a moment.";
		} else if (!isClassified) {
			icon = <Icon.AlertCircle size={14} />;
			message = stillNeedingAnAccount + " transaction" + (stillNeedingAnAccount === 1 ? "" : "s") + " still need" + (stillNeedingAnAccount === 1 ? "s" : "") + " classification.";
			variant = "warn";
		} else if (toReviewCount > 0) {
			icon = <Icon.Eye size={14} />;
			message = toReviewCount + " classification" + (toReviewCount === 1 ? "" : "s") + " need" + (toReviewCount === 1 ? "s" : "") + " your review on the To Review tab.";
			if (this.state.activeTab !== "to-review") {
				action = { label: "Go to To Review", onClick: () => this.update({ activeTab: "to-review" }) };
			}
		} else if (suspenseCount > 0) {
			icon = <Icon.AlertCircle size={14} />;
			message = suspenseCount + " transaction" + (suspenseCount === 1 ? " is" : "s are") + " in Suspense. You can still generate the AFS — the Suspense balance will appear on the balance sheet for you to clear later.";
			variant = "warn";
			action = { label: "Generate AFS anyway", onClick: this.onGenerateAfsClicked };
		} else if (!hasCompilation) {
			icon = <Icon.CheckCircle size={14} />;
			message = "All classifications reviewed. Ready to generate your AFS.";
			action = { label: "Generate AFS", onClick: this.onGenerateAfsClicked };
			variant = "success";
		} else if (!afsActive) {
			icon = <Icon.FileText size={14} />;
			message = "AFS generated. Review and export your financial statements.";
			action = { label: "Review AFS", onClick: () => {
				if (this.state.afsBalanceSheet) this.update({ afsActive: true });
				else this.onGenerateAfsClicked();
			}};
			variant = "success";
		} else {
			return null;
		}

		return (
			<div className={"bk-next-step bk-next-step--" + variant}>
				<span className="bk-next-step__icon">{icon}</span>
				<span className="bk-next-step__message">{message}</span>
				{action && (
					<button className="bk-next-step__action" onClick={action.onClick}>{action.label} <Icon.ArrowRight size={13} /></button>
				)}
			</div>
		);
	};

	renderFilteringSection = () => {
		return this.renderTabs();
	};

	renderEngagementProgress = () => {
		let { transactions } = this.props;
		let { priorYearTrialBalance, compilationData, afsActive, classifyingAi } = this.state;

		let txList = transactions || [];
		let totalTransactions = txList.length;

		let classifiedCount = txList.filter(t => {
			let c = t.classification || "";
			if (!c || c === "Unclassified" || c === "uncategorized") return false;
			if (t.canonical_code && t.canonical_code.startsWith("SUSPENSE.")) return false;
			return true;
		}).length;

		let reviewedCount = txList.filter(t => {
			if (!t.classification || t.classification === "Unclassified" || t.classification === "uncategorized") return false;
			if (t.canonical_code && t.canonical_code.startsWith("SUSPENSE.")) return false;
			let m = t.classification_method;
			return m === "manual" || m === "transfer" || m === "gl_reference" || m === "dbf_gifi" || t.cwClassified;
		}).length;

		let pendingReviewCount = txList.filter(t => BookkeepingService.awaitsReviewerSignOff({ transaction: t })).length;

		let suspenseCount = txList.filter(t => t.canonical_code && t.canonical_code.startsWith("SUSPENSE.") && !t.suspenseAccepted).length;
		let unclassifiedCount = this.getRowsStillNeedingAnAccount({ transactions: txList }).length;
		let settledCount = totalTransactions - unclassifiedCount;

		let hasCaseware = priorYearTrialBalance && priorYearTrialBalance.length > 0;
		let hasTransactions = totalTransactions > 0;
		let allReviewed = reviewedCount === classifiedCount && settledCount === totalTransactions && suspenseCount === 0;
		let allClassified = unclassifiedCount === 0;
		let hasCompilation = compilationData !== null && compilationData !== undefined;

		let classifyDetail = "Waiting for transactions";
		if (hasTransactions) {
			if (classifyingAi) {
				classifyDetail = "Classifying...";
			} else if (settledCount === 0 && suspenseCount === 0) {
				classifyDetail = "Waiting for classification";
			} else if (allReviewed) {
				classifyDetail = "\u2713 All reviewed";
			} else if (pendingReviewCount > 0 && suspenseCount > 0) {
				classifyDetail = pendingReviewCount + " to review, " + suspenseCount + " SUSPENSE";
			} else if (pendingReviewCount > 0) {
				classifyDetail = pendingReviewCount + " to review";
			} else if (suspenseCount > 0) {
				classifyDetail = suspenseCount + " SUSPENSE";
			} else if (unclassifiedCount > 0) {
				classifyDetail = settledCount + "/" + totalTransactions + " accounted for";
			} else {
				classifyDetail = "\u2713 " + reviewedCount + " reviewed";
			}
		}

		let classifyStatus = allReviewed ? "done" : hasTransactions ? "active" : "pending";

		let steps = [
			{
				label: "Import CaseWare",
				detail: hasCaseware ? priorYearTrialBalance.length + " accounts loaded" : "Not imported",
				status: hasCaseware ? "done" : "pending",
			},
			{
				label: "Bank Statements",
				detail: hasTransactions ? totalTransactions + " transactions" : "No CSVs uploaded",
				status: hasTransactions ? "done" : hasCaseware ? "active" : "pending",
			},
			{
				label: "Classify",
				detail: classifyDetail,
				detailPass: allReviewed,
				detailWarn: (pendingReviewCount > 0 || suspenseCount > 0) && classifiedCount > 0,
				status: classifyStatus,
			},
			{
				label: "Generate AFS",
				detail: hasCompilation
					? "Compilation prepared"
					: allReviewed
						? "\u2192 Ready to generate"
						: allClassified
							? (suspenseCount > 0 ? suspenseCount + " in Suspense (still ready)" : "Review pending items first")
							: "Classify transactions first",
				ready: !hasCompilation && allClassified,
				status: hasCompilation ? "done" : hasTransactions ? "active" : "pending",
				clickable: hasTransactions && !hasCompilation,
				onClick: this.onGenerateAfsClicked,
			},
			{
				label: "Review & Export",
				detail: afsActive ? "In progress" : hasCompilation ? "Ready to review" : "Waiting for AFS",
				status: afsActive ? "active" : hasCompilation ? "active" : "pending",
				clickable: hasCompilation && !afsActive,
				onClick: () => {
					if (this.state.afsBalanceSheet) {
						this.update({ afsActive: true });
					}
				},
			},
		];

		return (
			<div className="bk-progress">
				{steps.map(function (step, stepIndex) {
					let dotClass = "bk-progress__dot--" + step.status + (step.ready ? " bk-progress__dot--ready" : "");
					let showConnector = stepIndex < steps.length - 1;
					let connectorClass = step.status === "done" ? "bk-progress__connector--done" : "bk-progress__connector--pending";

					return (
						<React.Fragment key={stepIndex}>
							<div
								className={clsx("bk-progress__step", step.clickable && "bk-progress__step--clickable")}
								onClick={step.clickable ? step.onClick : undefined}
							>
								<div className={clsx("bk-progress__dot", dotClass)}>
									{step.status === "done"
										? <Icon.Check size={14} />
										: stepIndex + 1
									}
								</div>
								<div className="bk-progress__info">
									<span className={clsx("bk-progress__label", step.status === "pending" && "bk-progress__label--pending")}>{step.label}</span>
									<span className={clsx("bk-progress__detail", step.detailPass && "bk-progress__detail--pass", step.detailWarn && "bk-progress__detail--warn")}>{step.detail}</span>
								</div>
							</div>
							{showConnector && <div className={clsx("bk-progress__connector", connectorClass)} />}
						</React.Fragment>
					);
				})}
			</div>
		);
	};

	renderGstHstPostingStatus = () => {
		const { gstHstAccount } = this.props;
		const statusText = gstHstAccount
			? `GST/HST posts to ${gstHstAccount.accountNumber} — ${gstHstAccount.accountDescription}`
			: "No GST/HST account on this chart — tax stays in revenue and expenses";

		return (
			<div className="bk-table__hst-label" title="Read from the client's chart of accounts">
				<span className="bk-table__hst-label-text">{statusText}</span>
			</div>
		);
	};

	renderClientHeadings = () => {
		const { moreMenuOpen, hasTax } = this.state;
		const { onNewImport, onClearTransactions, transactions, gstHstAccount } = this.props;

		const suspenseTransactions = BookkeepingService.getSuspenseTransactions({ transactions });
		const suspenseTransactionCount = suspenseTransactions.length;

		const reviewDecisions = BookkeepingService.buildReviewDecisions({ transactions });
		const reviewIssues = BookkeepingService.getReviewIssues({ decisions: reviewDecisions });
		const totalIssueCount = reviewIssues.length;
		const resolvedIssueCount = reviewIssues.filter(issue => issue.status === "resolved").length;
		const issueButtonLabel = totalIssueCount > 0 ? `Issues (${resolvedIssueCount}/${totalIssueCount})` : "Issues";

		const { onHasTaxToggle, onExport } = this;

		return (
			<div className="bk-table__actions-bar">
				<div className="bk-table__actions-group">
					<Button text="Statements" view="primary" onClick={onNewImport} icon={<Icon.FileText size={16} />} title="The statements and files these books are built from" />

					{this.renderClassifyButton()}

					{(() => {
						const approvable = this.getApprovableTransactions();
						if (approvable.length === 0) return null;
						if (this.state.activeTab !== 'to-review' && this.state.activeTab !== 'auto-classified' && this.state.activeTab !== 'all') return null;
						return <Button text={"Accept All (" + approvable.length + ")"} view="secondary" onClick={this.onConfirmAll} icon={<Icon.CheckCircle size={16} />} />;
					})()}

					<Button text="Review" view="secondary" onClick={this.openAllocationReview} icon={<Icon.Eye size={18} />} />

					<Button text={issueButtonLabel} view="secondary" onClick={this.openIssuesReview} icon={<Icon.AlertCircle size={18} />} />

					<Button text="Generate AFS" view="secondary" onClick={this.onGenerateAfsClicked} icon={<Icon.FileText size={18} />} />
				</div>

				<div className="bk-table__actions-divider" />

				<div className="bk-table__actions-group">
					<Button text="Classify Selected" view="secondary" onClick={this.openBulkClassifyModal} icon={<Icon.ChevronsDown size={18} />} title="Classify every transaction you have selected" />
					<Button text="Add chart account" view="secondary" onClick={this.openAddClassificationModal} icon={<Icon.Plus size={18} />} title="Add an account to this client's chart of accounts" />

					<div className="bk-table__more-wrapper">
						<Button view="secondary" onClick={() => this.update({ moreMenuOpen: !moreMenuOpen })} icon={<Icon.MoreHorizontal size={18} />} title="More actions" />
						{moreMenuOpen && (
							<>
								<div className="bk-table__menu-backdrop" onClick={() => this.update({ moreMenuOpen: false })} />
								<div className="bk-table__more-menu">
									<button className="bk-table__more-item" onClick={() => { this.update({ moreMenuOpen: false }); this.openSettingsModal(); }}>
										<Icon.Settings size={15} /> Settings
									</button>
									<div className="bk-table__more-divider" />
									<button className="bk-table__more-item" onClick={() => { this.update({ moreMenuOpen: false }); onExport(); }}>
										<Icon.DownloadCloud size={15} /> Download General Ledger
									</button>
									<button className="bk-table__more-item" data-test="open-trial-balance-import" onClick={() => { this.update({ moreMenuOpen: false }); this.openTrialBalanceImport(); }}>
										<Icon.FileText size={15} /> Read a Trial Balance
									</button>
									<button className="bk-table__more-item" onClick={() => { this.update({ moreMenuOpen: false }); this.onOpenOpeningBalances(); }}>
										<Icon.Layers size={15} /> Opening Balances
									</button>
									<button className="bk-table__more-item" onClick={() => { this.update({ moreMenuOpen: false }); this.onExportSuspenseClicked(); }}>
										<Icon.AlertCircle size={15} /> Export Suspense ({suspenseTransactionCount})
									</button>
									{this.props.fiscalPeriod && (
										<button className="bk-table__more-item" onClick={() => this.update({ moreMenuOpen: false, subsequentActivityOpen: true })}>
											<Icon.Clock size={15} /> After Year-End Activity
										</button>
									)}
									{onClearTransactions && (
										<button className="bk-table__more-item bk-table__more-item--danger" onClick={() => { this.update({ moreMenuOpen: false }); onClearTransactions(); }}>
											<Icon.Trash2 size={15} /> Clear All
										</button>
									)}
								</div>
							</>
						)}
					</div>
				</div>

				<div className="bk-table__actions-spacer" />

				{this.renderGstHstPostingStatus()}
				{!gstHstAccount && (
					<div className="tax-checkbox">
						<Checkbox title="Taxes applicable" checked={hasTax} onChange={onHasTaxToggle} />
					</div>
				)}
			</div>
		);
	};
	

	getClassificationNamesStillNeedingAnAccount = () => {
		const { transactions } = this.props;
		const namesStillNeedingAnAccount = new Set();
		for (let t of (transactions || [])) {
			if (t.canonical_code) continue;
			if (t.classification && t.classification !== "Unclassified" && t.classification !== "uncategorized") {
				namesStillNeedingAnAccount.add(t.classification);
			}
		}
		return [...namesStillNeedingAnAccount].sort();
	};

	getAllClientAccountNames = () => {
		const everyAccountName = BookkeepingService.listEveryClientAccountName({
			clientChart: this.state.canonicalTaxonomy || [],
			usedClassificationNames: this.getClassificationNamesStillNeedingAnAccount(),
		});
		return everyAccountName;
	};


	getUsedClassificationNames = () => {
		const { transactions } = this.props;
		const usedNames = new Set();
		for (let t of (transactions || [])) {
			if (t.classification && t.classification !== "Unclassified" && t.classification !== "uncategorized") {
				usedNames.add(t.classification);
			}
		}
		return [...usedNames].sort();
	};

	getCanonicalLabelForAccount = accountName => {
		const canonicalCode = this.getCanonicalCodeForAccount(accountName);
		if (!canonicalCode) return "";

		const node = (this.state.canonicalTaxonomyOptions || []).find(option => option.canonical_code === canonicalCode);
		return node ? node.display_name : "";
	};

	renderCoaMappingGate = () => {
		if (!this.state.coaGateOpen) return null;

		const unmappedAccounts = this.getAccountsWithoutFsMapping();

		return (
			<ToolsModal open onClose={() => this.update({ coaGateOpen: false })} width={760}>
				<CoaMappingReview
					clientId={this.props.clientId}
					accounts={unmappedAccounts.map(name => ({ accountNumber: BookkeepingService.extractAccountNumberFromName(name), accountName: name }))}
					source="manual"
					title="Map your accounts before generating"
					subtitle={`${unmappedAccounts.length} account${unmappedAccounts.length > 1 ? "s aren't" : " isn't"} linked to an FS account yet — the AFS can't be built without these mappings. Review the suggestions below and confirm.`}
					hideSkip
					onComplete={this.onCoaGateComplete}
				/>
			</ToolsModal>
		);
	};

	getBankAccountCurrencies = () => {
		const { transactions } = this.props;
		const currencyByAccount = {};
		for (const transaction of (transactions || [])) {
			const accountName = transaction.accountName || transaction.bankAccountName;
			if (!accountName) continue;
			if (currencyByAccount[accountName] === undefined) currencyByAccount[accountName] = transaction.currency || "CAD";
		}

		const linkByAccountName = {};
		for (const link of this.state.bankAccountLinks) linkByAccountName[link.accountName] = link;

		const accountNames = Object.keys(currencyByAccount).sort();
		return accountNames.map(accountName => {
			const link = linkByAccountName[accountName] || {};
			return {
				accountName,
				currency: currencyByAccount[accountName],
				id: link.id || null,
				accountId: link.accountId || null,
				canonicalLabel: link.canonicalCode ? this.getCanonicalLabelForAccount(link.postsTo) : null,
				canonicalCode: link.canonicalCode || null,
			};
		});
	};

	loadBankAccountLinks = async () => {
		const answer = await BookkeepingService.fetchBankAccountLinks({
			clientId: this.props.clientId,
			wpfEngagementId: this.props.wpfEngagementId,
		});
		if (answer.error) {
			alert.error("The bank accounts could not be loaded: " + answer.error);
			return;
		}
		this.update({ bankAccountLinks: answer.bankAccounts });
	};

	getBankAccountCanonicalCodes = () => {
		const fromTheTransactionsPayload = this.props.bankSideAccounts || [];
		const fromTheLinksScreen = this.state.bankAccountLinks || [];
		const accounts = fromTheTransactionsPayload.length > 0 ? fromTheTransactionsPayload : fromTheLinksScreen;
		const codes = accounts.map(account => account.canonicalCode).filter(Boolean);
		return [...new Set(codes)];
	};

	getChartAccountOptions = () => {
		const clientChart = this.state.canonicalTaxonomy || [];
		return BookkeepingService.listChartAccountOptions({ clientChart });
	};

	onSetBankAccountChartAccount = async ({ bankAccountId, accountId }) => {
		const outcome = await BookkeepingService.tieBankAccountToChart({ bankAccountId, accountId, clientId: this.props.clientId });
		if (outcome.tied) alert.success(outcome.message);
		else alert.error(outcome.message);
		if (outcome.tied) this.loadBankAccountLinks();
	};

	onSetAccountCurrency = async ({ accountName, currency }) => {
		const { onTransactionsUpdate } = this.props;
		const changeByIdentity = await BookkeepingService.setAccountCurrency({ transactions: this.props.transactions, accountName, currency });
		if (Object.keys(changeByIdentity).length === 0) return;

		const { transactions: updatedTransactions } = BookkeepingService.applyRowChanges({ transactions: this.props.transactions, changeByIdentity });
		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);
	};

	onFlipAccountSigns = async ({ accountName }) => {
		const { transactions, onTransactionsUpdate } = this.props;

		const before = BookkeepingService.summarizeBankAccountSigns({ transactions, accountName });
		if (before.transactionCount === 0) return;

		const updatedTransactions = BookkeepingService.flipSignsForBankAccount({ transactions, accountName });
		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);

		const after = BookkeepingService.summarizeBankAccountSigns({ transactions: updatedTransactions, accountName });
		alert.success(`Flipped ${before.transactionCount} transactions on ${accountName}. Net is now ${UtilityService.formatCurrency(after.netAmount)}. Flip again to undo.`);
	};

	loadColumnPreferences = () => {
		const { columnWidths, hiddenColumns } = BookkeepingService.getTableColumnPreferences({ clientId: this.props.clientId });
		this.update({ columnWidths, hiddenColumns });
	};

	saveColumnPreferences = ({ columnWidths, hiddenColumns }) => {
		BookkeepingService.saveTableColumnPreferences({ clientId: this.props.clientId, columnWidths, hiddenColumns });
	};

	getTableGridTemplate = () => {
		const { bookkeepingTableHeadings, columnWidths, hiddenColumns } = this.state;
		const gridTemplate = BookkeepingService.buildTableGridTemplate({
			columns: bookkeepingTableHeadings,
			columnWidths,
			hiddenColumns,
			leadingWidth: CHECKBOX_COLUMN_WIDTH,
		});
		return gridTemplate;
	};

	isColumnHidden = columnKey => this.state.hiddenColumns.includes(columnKey);

	onToggleColumn = columnKey => {
		const isHidden = this.isColumnHidden(columnKey);
		const hiddenColumns = isHidden
			? this.state.hiddenColumns.filter(key => key !== columnKey)
			: [...this.state.hiddenColumns, columnKey];

		this.update({ hiddenColumns });
		this.saveColumnPreferences({ columnWidths: this.state.columnWidths, hiddenColumns });
	};

	onResetColumns = () => {
		this.update({ columnWidths: {}, hiddenColumns: [] });
		this.saveColumnPreferences({ columnWidths: {}, hiddenColumns: [] });
	};

	onColumnResizeStart = (columnKey, event) => {
		event.preventDefault();
		event.stopPropagation();

		const heading = this.state.bookkeepingTableHeadings.find(column => column.key === columnKey);
		if (!heading) return;

		const startingWidth = this.state.columnWidths[columnKey] || heading.width;
		const startingPointerX = event.clientX;

		const onPointerMove = moveEvent => {
			const draggedDistance = moveEvent.clientX - startingPointerX;
			const requestedWidth = startingWidth + draggedDistance;
			const width = Math.max(MINIMUM_COLUMN_WIDTH, Math.round(requestedWidth));
			this.update({ columnWidths: { ...this.state.columnWidths, [columnKey]: width } });
		};

		const onPointerUp = () => {
			window.removeEventListener("pointermove", onPointerMove);
			window.removeEventListener("pointerup", onPointerUp);
			document.body.classList.remove("bk-table__resizing");
			this.update({ resizingColumnKey: "" });
			this.saveColumnPreferences({ columnWidths: this.state.columnWidths, hiddenColumns: this.state.hiddenColumns });
		};

		this.update({ resizingColumnKey: columnKey });
		document.body.classList.add("bk-table__resizing");
		window.addEventListener("pointermove", onPointerMove);
		window.addEventListener("pointerup", onPointerUp);
	};

	getBankAccountSignSummary = accountName => {
		const summary = BookkeepingService.summarizeBankAccountSigns({ transactions: this.getLiveTransactions(), accountName });
		return summary;
	};

	renderStatementSettingsModal = () => {
		if (!this.state.statementSettingsOpen) return null;

		return (
			<StatementSettingsModal
				open={true}
				onClose={() => this.update({ statementSettingsOpen: false })}
				clientId={this.props.clientId}
				wpfEngagementId={this.props.wpfEngagementId}
				clientName={this.state.clientName}
				chartRows={this.state.canonicalTaxonomy || []}
				roundingCanonicalCode={this.state.roundingCanonicalCode || null}
				statementEntityName={this.state.statementEntityName || ""}
				dropOwnerNameBrackets={!!this.state.dropOwnerNameBrackets}
				signatoryCount={this.state.signatoryCount || 1}
				signatoryTitle={this.state.signatoryTitle || "Director"}
				jurisdiction={this.state.jurisdiction || ""}
				incorporationDate={this.state.incorporationDate || ""}
				principalActivities={this.state.principalActivities || ""}
				compilationReportCity={this.state.compilationReportCity || ""}
				compilationReportDate={this.state.compilationReportDate || ""}
				compilationReportSignature={this.state.compilationReportSignature || null}
				clientProvince={this.state.province || ""}
				onPresentationSettingsChange={this.applyPresentationSettingsToState}
			/>
		);
	};

	renderSettingsModal = () => {
		if (!this.state.settingsModalOpen) return null;

		return (
			<SettingsModal
				clientId={this.props.clientId}
				wpfEngagementId={this.props.wpfEngagementId}
				fiscalPeriod={this.props.fiscalPeriod}
				onFiscalPeriodChange={this.props.onFiscalPeriodChange}
				hideableColumns={this.getHideableColumns()}
				hiddenColumnKeys={this.state.hiddenColumns}
				onToggleColumn={this.onToggleColumn}
				onResetColumns={this.onResetColumns}
				transactions={this.getLiveTransactions()}
				accountNames={this.getAllClientAccountNames()}
				fsMappingReferenceLoaded={this.state.fsMappingReferenceLoaded}
				bankAccounts={this.getBankAccountCurrencies()}
				chartAccountOptions={this.getChartAccountOptions()}
				onPresentationSettingsChange={this.applyPresentationSettingsToState}
				onSetBankAccountChartAccount={this.onSetBankAccountChartAccount}
				onSetAccountCurrency={this.onSetAccountCurrency}
				onFlipAccountSigns={this.onFlipAccountSigns}
				getBankAccountSignSummary={this.getBankAccountSignSummary}
				classifications={this.state.classifications}
				classificationTaxMap={this.state.classificationTaxMap}
				customTaxRates={this.state.customTaxRates}
				taxRules={this.state.taxRules}
				onTaxRulesChange={this.onTaxRulesChange}
				onApplyTaxRules={this.onApplyTaxRules}
				taxOptions={this.getTaxOptions()}
				province={this.state.province}
				canonicalTaxonomyOptions={this.state.canonicalTaxonomyOptions}
				getCanonicalLabelForAccount={this.getCanonicalLabelForAccount}
				getChartAccountIdForName={this.getChartAccountIdForName}
				getCanonicalCodeForAccount={this.getCanonicalCodeForAccount}
				taxSettingsLoading={this.state.taxSettingsLoading}
				autoMappingAccounts={this.state.autoMappingAccounts}
				initialTab={this.state.settingsInitialTab}
				onProvinceSelect={this.onSettingsProvinceSelect}
				onFetchAllTaxCodes={this.onFetchAllTaxCodes}
				onAutoMapAccounts={this.onAutoMapAccounts}
				onOpenAddAccount={this.openAddClassificationModal}
				onClassificationsChange={this.onClassificationsChange}
				onTaxMapChange={this.onTaxMapChange}
				onCustomTaxRatesChange={this.onCustomTaxRatesChange}
				onTransactionsUpdate={this.props.onTransactionsUpdate}
				saveTransactions={this.saveTransactionsToServer}
				onClose={this.closeSettingsModal}
			/>
		);
	};

	renderAddClassificationModal = () => {
		if (!this.state.addClassificationModalOpen) return null;

		return (
			<AddClassificationModal
				taxOptions={this.getTaxOptions()}
				canonicalOptions={this.state.canonicalTaxonomyOptions || []}
				onAdd={this.onAddAccount}
				onClose={this.closeAddClassificationModal}
			/>
		);
	};

	renderReviewModal = () => {
		const { reviewModalOpen, reviewFeedback } = this.state;
		const { transactions } = this.props;

		const summary = BookkeepingService.buildClassificationSummary({ transactions });
		const accuracyClass = percent => percent === 100 ? "bk-review__pct--perfect" : percent >= 80 ? "bk-review__pct--ok" : "bk-review__pct--poor";

		const reviewFooter = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={() => this.update({ reviewModalOpen: false })}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onConfirmReview} disabled={!reviewFeedback.trim()}>Confirm &amp; download</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={reviewModalOpen} onClose={() => this.update({ reviewModalOpen: false })} title="Review before downloading" width={560} footer={reviewFooter}>
				<div className="bk-review__body">
					<div className="input-field bk-review__field-spacer">
						<div className="input-field__title">Classification Summary</div>
						<div className="bk-review__field-value--multiline">
							<div>Total transactions: <strong>{summary.totalCount}</strong></div>
							<div>Classified from GL reference: <strong>{summary.glReferenceCount}</strong></div>
							<div>Classified by the AI or by hand: <strong>{summary.autoClassifiedCount}</strong></div>
							<div>Transfers: <strong>{summary.transferCount}</strong></div>
							<div>Unclassified: <strong>{summary.unclassifiedCount}</strong></div>
							<div>Manually changed: <strong>{summary.manualChangeCount}</strong></div>
							<div className="bk-review__accuracy-block">
								<div>Coverage: <strong className={accuracyClass(summary.coveragePercent)}>{summary.coveragePercent}%</strong> <span className="bk-review__pct-note">({summary.classifiedCount} of {summary.totalCount} classified)</span></div>
								{summary.suggestionAccuracyPercent !== null && (
									<div>Suggestion accuracy: <strong className={accuracyClass(summary.suggestionAccuracyPercent)}>{summary.suggestionAccuracyPercent}%</strong> <span className="bk-review__pct-note">(of pre-classified, kept as-is)</span></div>
								)}
							</div>
						</div>
					</div>
					{summary.manualChanges.length > 0 && (
						<div className="bk-review__changes-list">
							<div className="input-field__title bk-review__changes-title">Manual Changes</div>
							{summary.manualChanges.map((t, i) => (
								<div key={i} className="bk-review__change-item">
									<strong>{t.description}</strong>: <span className="bk-review__change-source">{t.aiClassification ? `(AI${t.confidence != null ? `, ${Math.round(t.confidence * 100)}%` : ""})` : (t.glClassified ? "(GL)" : "(CW)")}</span> {t.aiClassification || t.cwClassification || "Unclassified"} \u2192 {t.classification}
								</div>
							))}
						</div>
					)}
					<div className="bk-review__feedback-section">
						<TextAreaField
							title="Feedback on the Tool"
							name="reviewFeedback"
							value={reviewFeedback}
							onChange={e => this.update({ reviewFeedback: e.target.value })}
							placeholder="How did it go? Anything we should improve?"
						/>
					</div>
					<div className="bk-review__field-spacer">
						<Checkbox
							title="Flip signs in the download (invert debits and credits)"
							checked={this.state.invertDownloadSigns}
							onChange={() => this.update({ invertDownloadSigns: !this.state.invertDownloadSigns })}
						/>
						{this.state.invertDownloadSigns && (
							<p className="bk-review__invert-note">Every amount in the downloaded file will be negated — for importing into software that expects the opposite convention. The tool's own data and the emailed report are unchanged.</p>
						)}
					</div>
				</div>
			</ToolsModal>
		);
	};

	renderApproveAllModal = () => {
		const { approveAllModalOpen } = this.state;
		if (!approveAllModalOpen) return null;

		const toConfirm = this.getApprovableTransactions();
		const { activeTab } = this.state;

		const tabLabel = activeTab === 'to-review' ? 'To Review' : activeTab === 'auto-classified' ? 'From the GL' : 'all';

		const groups = {};
		toConfirm.forEach(t => {
			const key = t.classification || "Unclassified";
			if (!groups[key]) groups[key] = 0;
			groups[key]++;
		});

		const approveFooter = (
			<>
				<span />
				<span className="tk-modal-actions">
					<button type="button" className="tk-button tk-button--secondary" onClick={() => this.update({ approveAllModalOpen: false })}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.onConfirmAll}>Accept {toConfirm.length} items</button>
				</span>
			</>
		);

		return (
			<ToolsModal open={true} onClose={() => this.update({ approveAllModalOpen: false })} title={`Accept ${toConfirm.length} classifications`} width={480} footer={approveFooter}>
				<div className="bk-approve__body">
					<p className="bk-approve__para">This marks <strong>{toConfirm.length} classifications</strong> from <strong>{tabLabel}</strong> as reviewed and moves them to Approved.</p>
					<p className="bk-approve__para">It changes nothing about how future transactions are classified, and you can still reclassify any of these rows afterwards.</p>
					<div className="bk-approve__summary">
						<div className="bk-approve__summary-title">Classifications being accepted:</div>
						{Object.entries(groups).sort((a, b) => b[1] - a[1]).map(([name, count]) => (
							<div key={name} className="bk-approve__summary-row">
								<span>{name}</span>
								<span className="bk-approve__summary-count">{count}</span>
							</div>
						))}
					</div>
				</div>
			</ToolsModal>
		);
	};

	renderClassifyButton() {
		const { classifyingAi } = this.state;
		const { transactions } = this.props;

		const unclassifiedCount = this.getRowsStillNeedingAnAccount({ transactions: transactions || [] }).length;
		const hasUnclassified = unclassifiedCount > 0;
		const hasSomeClassified = (transactions || []).some(t => t.classification && t.classification !== "Unclassified" && t.classification !== "uncategorized");
		const isDisabled = classifyingAi || !transactions || transactions.length === 0 || !hasUnclassified;
		const disabledReason = !hasUnclassified ? "All transactions are classified" : "";
		const buttonText = classifyingAi ? "Classifying..." : `Classify (${unclassifiedCount})`;

		if (!classifyingAi && hasSomeClassified && !hasUnclassified) return null;

		return (
			<span title={disabledReason} className="bk-classify-btn-wrap">
				<Button
					text={buttonText}
					view="secondary"
					onClick={isDisabled ? null : this.onClassify}
					icon={classifyingAi ? <Icon.Loader size={18} /> : <Icon.Send size={18} />}
					disabled={isDisabled}
				/>
			</span>
		);
	}

	renderClassifyMissingFieldsModal() {
		if (!this.state.classifyMissingFieldsModalOpen) return null;

		return (
			<ClassifyMissingFieldsModal
				needsProvince={!this.state.province}
				needsIndustry={!this.state.industry}
				onSave={this.onClassifyMissingFieldsSave}
				onClose={this.closeClassifyMissingFieldsModal}
			/>
		);
	}

	closeClassifyMissingFieldsModal = () => this.update({ classifyMissingFieldsModalOpen: false });

	onClassifyMissingFieldsSave = async ({ province: pickedProvince, industry: pickedIndustry }) => {
		const { province, industry } = this.state;
		const { clientId, transactions } = this.props;

		const newProvince = province || pickedProvince;
		const newIndustry = industry || pickedIndustry;

		await this.update({
			province: newProvince,
			industry: newIndustry,
			classifyMissingFieldsModalOpen: false,
		});

		const changed = BookkeepingService.pickWhatChangedSinceTheServerSawIt({
			transactions,
			serverHas: this.serverHasTransactions,
		});

		try {
			await BookkeepingService.saveEngagementTransactions({
				engagementId: null,
				wpfEngagementId: this.props.wpfEngagementId,
				clientId,
				transactions: changed.transactions,
				removeTransactionIds: changed.removeTransactionIds,
				province: newProvince,
				csrsChecklist: { operatingProfile: { industry: newIndustry } },
			});
		} catch (err) {
			console.error("Failed to persist province/industry:", err);
		}

		this.onClassify();
	}

	getApprovableTransactions = () => {
		const shownInThisTab = this.getFilteredTransactions();
		const approvable = shownInThisTab.filter(
			transaction => transaction.canonical_code && transaction.classification_status !== "approved"
		);
		return approvable;
	};

	saveTransactionsToServer = async (transactionsOverride, options = {}) => {
		const runAfterTheOneBefore = this.whenTheLastSaveFinishes.then(() => this.sendTransactionsToServer(transactionsOverride, options));
		this.whenTheLastSaveFinishes = runAfterTheOneBefore.then(() => undefined, () => undefined);
		return runAfterTheOneBefore;
	};

	sendTransactionsToServer = async (transactionsOverride, options = {}) => {
		const { clientId } = this.props;
		const { province, generalLedgerEntries } = this.state;
		const transactions = transactionsOverride || this.props.transactions;
		if (!transactions || transactions.length === 0) return true;

		const shouldPersistGlEntries = !!options.persistGlEntries && generalLedgerEntries && generalLedgerEntries.length > 0;

		const changed = BookkeepingService.pickWhatChangedSinceTheServerSawIt({
			transactions,
			serverHas: this.serverHasTransactions,
		});
		try {
			let result = await BookkeepingService.saveEngagementTransactions({
				engagementId: null,
				wpfEngagementId: this.props.wpfEngagementId,
				clientId,
				transactions: changed.transactions,
				removeTransactionIds: changed.removeTransactionIds,
				province,
				provinceChosenByAPerson: this.state.provinceChosenByAPerson || undefined,
				fiscalYearEnd: this.getFiscalYearEndToSeedANewEngagement(),
				generalLedgerEntries: shouldPersistGlEntries ? generalLedgerEntries : undefined,
			});
			if (result && result.skipped_as_already_on_file > 0) {
				alert.info(result.skipped_as_already_on_file + " transaction(s) were already on this engagement and were not added again.");
			}
			if (!result || result.error) {
				alert.error("Failed to save transactions. Your work may not be persisted.");
				return false;
			}
			await this.reloadTransactionsFromServer();
			return true;
		} catch (err) {
			alert.error("Failed to save transactions. Check your connection and try again.");
			console.error("saveTransactionsToServer error:", err);
			return false;
		}
	};

	acceptClassifications = async ({ transactions: transactionsToAccept }) => {
		const { onTransactionsUpdate } = this.props;
		const acceptedIdentities = new Set(
			transactionsToAccept.map(transaction => BookkeepingService.getTransactionIdentity({ transaction }))
		);

		const updatedTransactions = (this.props.transactions || []).map(candidate =>
			acceptedIdentities.has(BookkeepingService.getTransactionIdentity({ transaction: candidate }))
				? { ...candidate, classification_status: "approved" }
				: candidate
		);

		onTransactionsUpdate(updatedTransactions);
		await this.saveTransactionsToServer(updatedTransactions);

		const added = await this.addNewAccountsToCoa(this.collectNewAccounts(transactionsToAccept));
		if (added > 0) UtilityService.alert({ type: "success", text: `Added ${added} ${added === 1 ? "account" : "accounts"} to your chart of accounts.` });
	};

	onConfirmAll = async () => {
		const toAccept = this.getApprovableTransactions();
		if (toAccept.length === 0) return;

		await this.update({ approveAllModalOpen: false });
		await this.acceptClassifications({ transactions: toAccept });
		alert.info(`${toAccept.length} classification${toAccept.length === 1 ? "" : "s"} accepted.`, { autoClose: 6000 });
	};

	onConfirmClassification = async transaction => {
		await this.acceptClassifications({ transactions: [transaction] });
	};

	rerunChecksAndRefreshStatements = async ({ approvalContext }) => {
		let rerunResult = await BookkeepingService.rerunCrossChecks({
			clientId: this.props.clientId,
			wpfEngagementId: this.props.wpfEngagementId,
			previousResults: this.state.crossCheckResults || [],
			approvalContext: approvalContext || null,
		});
		if (!rerunResult || !rerunResult.crossCheckResults || rerunResult.crossCheckResults.length === 0) return;

		let refreshed = { crossCheckResults: rerunResult.crossCheckResults, crossCheckFacts: rerunResult.crossCheckFacts || this.state.crossCheckFacts };
		if (rerunResult.afs && rerunResult.afs.balanceSheet) {
			refreshed.afsBalanceSheet = rerunResult.afs.balanceSheet;
			refreshed.afsIncomeStatement = rerunResult.afs.incomeStatement || this.state.afsIncomeStatement;
			refreshed.roundedPresentation = rerunResult.roundedPresentation || null;
			refreshed.statementRows = rerunResult.statementRows || null;
			refreshed.approvalBlock = rerunResult.approvalBlock || null;
		}
		await this.update(refreshed);
	};

	onCompilationRunComplete = async ({ state, afs, yearEnd, entityName, unresolvedItems, suspenseResolutions, compilationData, csrsChecklist, crossCheckResults, crossCheckFacts, comparativeProvenance, compilationReportSignature, compilationReport, periodDescription, roundedPresentation, statementRows, approvalBlock, roundingCanonicalCode, agentResult, retainedEarnings, ppeNote, isFinalized }) => {
		await this.update({
			compilationRunWizardOpen: false,
			compilationData: compilationData || this.state.compilationData,
			cwYearEnd: yearEnd || this.state.cwYearEnd,
			clientName: entityName || this.state.clientName,
		});

		if (csrsChecklist) {
			BookkeepingService.saveEngagementTransactions({
				clientId: this.props.clientId,
				wpfEngagementId: this.props.wpfEngagementId,
				transactions: [],
				csrsChecklist,
			}).catch(() => {});
		}
		if (!afs || !afs.balanceSheet) return;

		if (unresolvedItems && unresolvedItems.length > 0 && suspenseResolutions) {
			const { transactions, onTransactionsUpdate } = this.props;
			const acceptedDescriptions = new Set(
				unresolvedItems
					.filter((_, idx) => suspenseResolutions[idx])
					.map(item => item.raw_description)
			);
			if (acceptedDescriptions.size > 0) {
				const updatedTransactions = transactions.map(t =>
					acceptedDescriptions.has(t.description) ? { ...t, suspenseAccepted: true } : t
				);
				onTransactionsUpdate(updatedTransactions);
			}
		}

		await this.update({
			afsBalanceSheet: afs.balanceSheet,
			afsIncomeStatement: afs.incomeStatement,
			afsYearEnd: afs.yearEnd || this.state.cwYearEnd,
			afsClientProfile: afs.clientProfile || null,
			afsRetainedEarnings: retainedEarnings || null,
			afsIsFinalized: !!isFinalized,
			afsPpeNote: ppeNote || null,
			compilationData: compilationData || state || {},
			crossCheckResults: crossCheckResults || null,
			crossCheckFacts: crossCheckFacts || null,
			comparativeProvenance: comparativeProvenance || null,
			compilationReportSignature: compilationReportSignature || null,
			compilationReport: compilationReport || null,
			roundedPresentation: roundedPresentation || null,
			statementRows: statementRows || null,
			approvalBlock: approvalBlock || null,
			roundingCanonicalCode: roundingCanonicalCode || null,
			periodDescription: periodDescription || null,
			agentResult: agentResult || null,
			afsLoading: false,
			afsActive: true,
		});

		try {
			await this.rerunChecksAndRefreshStatements({ approvalContext: { type: "post_compilation_sync" } });
		} catch (_) {}
	};

	onDownloadAFS = async () => {
		const { clientId, wpfEngagementId } = this.props;

		await this.update({ afsDownloading: true });
		const { error } = await AFSService.downloadAFS({ clientId, wpfEngagementId });
		if (error) UtilityService.alert({ type: "error", text: error });
		await this.update({ afsDownloading: false });
	};

	onDownloadPDF = async (noteTexts, noteIds, customNotes) => {
		const { clientId, transactions } = this.props;
		const { afsBalanceSheet, afsIncomeStatement, afsYearEnd, afsClientProfile, afsRetainedEarnings, afsPpeNote, compilationData, clientName, adjustingEntries } = this.state;

		let enriched = CurrentYearMovementsService.applyCurrentYearMovements({ balanceSheet: afsBalanceSheet, incomeStatement: afsIncomeStatement, transactions, adjustingEntries, gstHstAccount: this.props.gstHstAccount });

		let customNoteTitles = BookkeepingService.buildCustomNoteTitleMap({ customNotes });

		await this.update({ afsDownloading: true });
		const { error } = await AFSService.downloadPDF({
			clientId,
			entityName: clientName,
			yearEnd: this.getFiscalYearEndOnRecord() || afsYearEnd,
			balanceSheet: enriched.balanceSheet,
			incomeStatement: enriched.incomeStatement,
			retainedEarnings: afsRetainedEarnings,
			compilationData,
			clientProfile: afsClientProfile,
			ppeNote: afsPpeNote,
			noteTexts: noteTexts || {},
			noteIds: noteIds || undefined,
			customNoteTitles: customNoteTitles,
		});
		if (error) UtilityService.alert({ type: "error", text: error });
		await this.update({ afsDownloading: false });
	};

	render() {
		const { loading, selectAll, bookkeepingTableHeadings, afsActive, afsLoading, afsDownloading, afsBalanceSheet, afsIncomeStatement, afsYearEnd, afsGlEntries, afsNotes, afsBlMonthly, afsAnnotations, afsClientProfile, afsPpeNote, afsRetainedEarnings, afsIsFinalized, adjustingEntries, compilationRunWizardOpen, clientName } = this.state;

		if (afsActive) {
			return (
				<div className="bookkeeping-tables">
					{this.renderEngagementProgress()}
					{this.renderSettingsModal()}
					{this.renderStatementSettingsModal()}
				{this.renderTrialBalanceImportModal()}
					{this.renderTrialBalanceImportModal()}
					{this.renderManualTransferModal()}
					{this.renderAddClassificationModal()}
					{this.renderCoaMappingGate()}
					{this.renderAllocationReview()}
					<CompilationRunWizard
						open={compilationRunWizardOpen}
						onClose={() => this.update({ compilationRunWizardOpen: false })}
						onComplete={this.onCompilationRunComplete}
						clientId={this.props.clientId}
						clientName={clientName}
						province={this.getProvinceOnRecord()}
						yearEnd={this.getFiscalYearEndOnRecord()}
						csrsChecklist={this.props.csrsChecklist}
					/>
					<AFSWalkthrough
						balanceSheet={afsBalanceSheet}
						incomeStatement={afsIncomeStatement}
						yearEnd={this.getFiscalYearEndOnRecord() || afsYearEnd}
						loading={afsLoading}
						downloading={afsDownloading}
						showComparatives={this.state.compilationData?.comparatives !== false}
						comparativeProvenance={this.state.comparativeProvenance}
						compilationReportSignature={this.state.compilationReportSignature}
						compilationReport={this.state.compilationReport}
						onOpenStatementSettings={() => this.update({ statementSettingsOpen: true })}
						onChangeStatementSetting={this.onChangeStatementSetting}
						statementEntityName={this.state.statementEntityName || ""}
						periodDescription={this.state.periodDescription}
						roundedPresentation={this.state.roundedPresentation}
						statementRows={this.state.statementRows}
						approvalBlock={this.state.approvalBlock}
						onMoveAccount={this.onMoveAccount}
						onSortSection={this.onSortSection}
						onRenameAccount={this.onRenameAccount}
						accountCaptions={this.state.accountCaptions || {}}
						sectionSortOrder={this.state.sectionSortOrder || {}}
						compilationData={this.state.compilationData}
						entityName={clientName}
						clientProfile={afsClientProfile}
						ppeNote={afsPpeNote}
						retainedEarnings={afsRetainedEarnings}
						isFinalized={afsIsFinalized}
						glEntries={afsGlEntries}
						transactions={this.getLiveTransactions()}
						adjustingEntries={adjustingEntries}
						gstHstAccount={this.props.gstHstAccount}
						onOpenIssues={this.openIssuesReview}
						chartOfAccounts={this.state.canonicalTaxonomy && this.state.canonicalTaxonomy.length > 0 ? this.state.canonicalTaxonomy : this.state.priorYearTrialBalance}
						canonicalTaxonomyOptions={this.state.canonicalTaxonomyOptions || []}
						onCreateChartOfAccount={async (account) => {
							let result = await BookkeepingService.createChartOfAccount({ clientId: this.props.clientId, ...account });
							if (!result) return { error: "Network error creating account" };
							if (result.error) return { error: result.error };
							let resolved = result.account || result.existing;
							if (!resolved) return { error: "Account creation returned no data" };
							let existing = this.state.canonicalTaxonomy || [];
							let alreadyIn = existing.some(a => a.accountNumber === resolved.accountNumber);
							if (!alreadyIn) await this.update({ canonicalTaxonomy: [...existing, resolved] });
							return { account: resolved, conflict: !!result.conflict };
						}}
						onAdjustingEntriesChange={async (entries) => {
							this.update({ adjustingEntries: entries });
							await BookkeepingService.saveAdjustingEntries({ clientId: this.props.clientId, wpfEngagementId: this.props.wpfEngagementId, entries: entries });
							await this.rerunChecksAndRefreshStatements({ approvalContext: { type: "adjusting_entry" } });
						}}
						onTransactionClassificationChange={(transaction, newClassification) => this.handleClassificationChange(transaction, newClassification)}
						classifications={this.getSortedClassifications()}
						reviewNotes={afsNotes}
						blMonthly={afsBlMonthly}
						annotations={afsAnnotations}
						crossCheckResults={this.state.crossCheckResults}
						crossCheckFacts={this.state.crossCheckFacts}
						initialAgentResult={this.state.agentResult}
						onAgentModeComplete={async () => {
							try {
								let ajeResult = await BookkeepingService.loadAdjustingEntries({ clientId: this.props.clientId, wpfEngagementId: this.props.wpfEngagementId });
								if (ajeResult && ajeResult.entries) {
									this.update({ adjustingEntries: ajeResult.entries });
								}
								await this.rerunChecksAndRefreshStatements({ approvalContext: null });
							} catch (_) {}
						}}
						onApproveReclassification={async ({ transaction_id, transaction_description, to_canonical_code, scope }) => {
							let { transactions, onTransactionsUpdate } = this.props;
							let updated = transactions.map(function (transaction) {
								if (scope === "single" && transaction_id) {
									return transaction.id === transaction_id ? { ...transaction, canonical_code: to_canonical_code, classification_method: "manual" } : transaction;
								}
								return transaction.description === transaction_description ? { ...transaction, canonical_code: to_canonical_code, classification_method: "manual" } : transaction;
							});
							onTransactionsUpdate(updated);
							await this.saveTransactionsToServer(updated);
							await this.rerunChecksAndRefreshStatements({ approvalContext: { type: "reclassification", description: transaction_description } });
						}}
						onApproveAccountMapping={async ({ account_number, account_name, canonical_code }) => {
							await BookkeepingService.mapCoaSave({
								clientId: this.props.clientId,
								mappings: [{ accountNumber: account_number, accountName: account_name, canonical_code: canonical_code, source: "manual" }],
							});
							await this.rerunChecksAndRefreshStatements({ approvalContext: { type: "account_mapping", account_name: account_name } });
						}}
						onApproveContextChange={async ({ field, value }) => {
							await BookkeepingService.saveEngagementTransactions({
								clientId: this.props.clientId,
								wpfEngagementId: this.props.wpfEngagementId,
								transactions: [],
								csrsChecklist: { ...this.props.csrsChecklist, [field]: value },
							});
							await this.rerunChecksAndRefreshStatements({ approvalContext: { type: "context_change", field: field } });
						}}
						onAcknowledgeCrossCheck={({ check_id, reason, allowed_reason }) => {
							this.update(function (prev) {
								let acknowledged = { ...(prev.crossCheckAcknowledged || {}), [check_id]: true };
								return { crossCheckAcknowledged: acknowledged };
							});
						}}
						persistedNoteTexts={this.state.persistedNoteTexts}
						persistedCustomNotes={this.state.persistedCustomNotes}
						onNotesChange={({ noteTexts, customNotes }) => {
							this.update({ persistedNoteTexts: noteTexts, persistedCustomNotes: customNotes });
							BookkeepingService.saveAllFsNotes({ clientId: this.props.clientId, wpfEngagementId: this.props.wpfEngagementId, noteTexts: noteTexts, customNotes: customNotes });
						}}
						onSignOff={async () => {
							let result = await BookkeepingService.signOffEngagement({
								clientId: this.props.clientId,
								signoffLevel: "preparer",
								designation: "CPA",
								attestation: "I have reviewed the financial information and confirm it is complete and accurate for the purposes of this compilation engagement.",
								retainedEarnings: this.state.afsRetainedEarnings || null,
								noticeToReader: this.state.compilationData?.compilationNotice || null,
								notesToFs: this.state.persistedNoteTexts || null,
							});
							if (result && result.state === "FINALIZED") {
								await this.update({ engagementSignedOff: true });
							}
						}}
						signedOff={!!this.state.engagementSignedOff}
						clientRequestData={this.state.clientRequestData}
						onAddClientRequest={async (item) => {
							let result = await BookkeepingService.addClientRequestItems({ clientId: this.props.clientId, items: [item] });
							if (result && result.requestId) {
								let refreshed = await BookkeepingService.getClientRequests({ clientId: this.props.clientId });
								this.update({ clientRequestData: refreshed });
							}
						}}
						onSendClientRequests={async ({ requestId, contactEmail }) => {
							let result = await BookkeepingService.sendClientRequests({ requestId, contactEmail });
							let refreshed = await BookkeepingService.getClientRequests({ clientId: this.props.clientId });
							this.update({ clientRequestData: refreshed });
							return result;
						}}
						onUpdateClientRequestItem={async (item) => {
							let reqData = this.state.clientRequestData;
							if (reqData && reqData.request) {
								await BookkeepingService.updateClientRequestItems({ requestId: reqData.request.id, items: [item] });
								let refreshed = await BookkeepingService.getClientRequests({ clientId: this.props.clientId });
								this.update({ clientRequestData: refreshed });
							}
						}}
						onDeleteClientRequestItem={async (itemId) => {
							await BookkeepingService.deleteClientRequestItem({ itemId });
							let refreshed = await BookkeepingService.getClientRequests({ clientId: this.props.clientId });
							this.update({ clientRequestData: refreshed });
						}}
						clientEmail={this.props.clientEmail || ""}
						onClose={() => this.update({ afsActive: false })}
						clientId={this.props.clientId}
						onDownload={this.onDownloadAFS}
						onDownloadPDF={this.onDownloadPDF}
					/>
				</div>
			);
		}

		if (loading) {
			return (
				<div className="container-loader">
					<Loader />
				</div>
			);
		}

		const filteredTransactions = this.getFilteredTransactions();
		const rowsSettledAsTransfers = this.getRowsAlreadySettledAsTransfers();
		const statementTypeByCanonicalCode = this.getStatementTypeByCanonicalCode();
		const rowsWorthRendering = this.readRowsWorthRendering({
			firstRowOnScreen: this.state.firstRowOnScreen,
			rowsThatFitOnScreen: this.state.rowsThatFitOnScreen,
		});
		const { duplicateKeys } = BookkeepingService.findLikelyDuplicateTransactions({ transactions: this.props.transactions });

		return (
			<div className="bookkeeping-tables">
				{this.renderSettingsModal()}
					{this.renderStatementSettingsModal()}
				{this.renderManualTransferModal()}
				{this.renderAllocationReview()}
				{this.renderAddClassificationModal()}
				{this.renderCoaMappingGate()}
				{this.renderReviewModal()}
				{this.renderOpeningBalancesModal()}
				{this.renderApproveAllModal()}
				{this.state.bulkClassifyModalOpen && (
					<BulkClassifyModal
						transactions={this.getSelectedTransactions()}
						classifications={this.getSortedClassifications()}
						taxOptions={this.getTaxOptions()}
						hasTax={this.taxesApply()}
						onApply={this.onBulkClassifyApply}
						onClose={this.closeBulkClassifyModal}
					/>
				)}
				{this.state.classifyModalTransaction && (
					<ClassifyTransactionModal
						transaction={this.state.classifyModalTransaction}
						classifications={this.getSortedClassifications()}
						taxOptions={this.getTaxOptions()}
						customTaxRates={this.state.customTaxRates}
						clientId={this.props.clientId}
						hasTax={this.taxesApply()}
						onClassificationChange={this.handleClassificationChange}
						onTaxChange={this.handleTransactionTaxChange}
						onCustomTaxRatesChange={this.onCustomTaxRatesChange}
						onMarkAsTransfer={this.onMarkTransactionAsTransfer}
						onClose={this.closeClassifyModal}
					/>
				)}
				{this.renderClassifyMissingFieldsModal()}
				{this.state.archiveModalTransactions && (
					<ArchiveTransactionsModal
						transactions={this.state.archiveModalTransactions}
						onConfirm={this.onArchiveConfirmed}
						onClose={this.closeArchiveModal}
					/>
				)}
				{this.state.subsequentActivityOpen && this.props.fiscalPeriod && (
					<SubsequentActivityModal
						clientId={this.props.clientId}
						fiscalPeriod={this.props.fiscalPeriod}
						onClose={() => this.update({ subsequentActivityOpen: false })}
					/>
				)}
				{this.state.newAccountReview && this.state.newAccountReview.length > 0 && (
					<ToolsModal
						open
						onClose={this.dismissNewAccountReview}
						title="New accounts from this classification"
						width={560}
						footer={
							<>
								<span />
								<span className="tk-modal-actions">
									<button type="button" className="tk-button tk-button--secondary" onClick={this.dismissNewAccountReview}>Not now</button>
									<button type="button" className="tk-button tk-button--primary" onClick={this.addReviewedAccountsToCoa} disabled={this.state.newAccountReview.filter(a => a.checked).length === 0}>
										Add {this.state.newAccountReview.filter(a => a.checked).length} to chart of accounts
									</button>
								</span>
							</>
						}
					>
						<p className="bk-new-accounts__subtitle">
							The classifier used these accounts that aren't in your chart of accounts yet. Confident matches are pre-checked — review and add the ones you want. You can also do this later.
						</p>
						<div className="bk-new-accounts__list">
							{this.state.newAccountReview.map(a => (
								<label key={a.code} className="bk-new-accounts__row">
									<input type="checkbox" checked={a.checked} onChange={() => this.toggleNewAccountCheck(a.code)} />
									<span className="bk-new-accounts__name">{a.name}</span>
									<span className={clsx("bk-new-accounts__verdict", a.confident ? "bk-new-accounts__verdict--confident" : "bk-new-accounts__verdict--review")}>{a.confident ? "Confident" : "Needs review"}</span>
									<span className="bk-new-accounts__count">{a.txCount} txn{a.txCount !== 1 ? "s" : ""}</span>
								</label>
							))}
						</div>
					</ToolsModal>
				)}
				<CompilationRunWizard
					open={compilationRunWizardOpen}
					onClose={() => this.update({ compilationRunWizardOpen: false })}
					onComplete={this.onCompilationRunComplete}
					clientId={this.props.clientId}
					clientName={clientName}
					transactions={this.getLiveTransactions()}
					cwFiles={this.props.initialCwFiles}
					province={this.getProvinceOnRecord()}
					yearEnd={this.getFiscalYearEndOnRecord()}
					csrsChecklist={this.props.csrsChecklist}
				/>
				{this.renderEngagementProgress()}
				<div className="bk-surface-card bk-card--toolbar">
					{this.renderClientHeadings()}
				</div>
				{this.renderNextStepBanner()}
				<div className="bk-surface-card bk-card--status">
					{this.renderFilteringSection()}
				</div>
				<div className="bk-surface-card bk-card--table">
					{this.renderClassifyingOverlay()}
					{this.state.activeTab === "transfers" ? this.renderTransfersView() : (
					<>
					<div className="bk-table-card__toolbar">
						<SearchInput
							placeholder="Search transactions..."
							onChange={this.onSearchTyped}
							defaultValue={this.state.search}
						/>
						{this.renderBankFilter()}
					</div>
					{this.renderBulkSelectionBar()}
					<div className="scroll-container" ref={this.scrollAreaRef} onScroll={this.onTableScrolled}>
					<div
						className={clsx("t t--compact bk-table", this.state.hiddenColumns.map(columnKey => `bk-table--hide-${columnKey}`))}
						style={{ "--bk-grid-template": this.getTableGridTemplate() }}
					>
						<div className={"t__header"}>
							<div className="t__header__item clients__header__item--checkbox">
								<label className={clsx("checkbox", "checkbox--small", "engs-row-item--checkbox")}>
									<input type="checkbox" checked={selectAll} onChange={this.handleSelectAll} />
								</label>
							</div>
							{bookkeepingTableHeadings.map(heading => (
								<div key={heading.key} className={clsx("t__header__item", heading.className)} onClick={heading.name ? () => this.sortBy(heading.name.toLowerCase()) : undefined}>
									{heading.name}
									{heading.name && this.renderSortIcon(heading.name.toLowerCase())}
									{!heading.fixed && (
										<span
											className={clsx("bk-table__resize-handle", this.state.resizingColumnKey === heading.key && "bk-table__resize-handle--active")}
											title={`Drag to resize ${heading.name}`}
											onPointerDown={e => this.onColumnResizeStart(heading.key, e)}
											onClick={e => e.stopPropagation()}
										/>
									)}
								</div>
							))}
						</div>

						{rowsWorthRendering.from > 0 && (
							<div className="bk-table__offscreen-rows" style={{ "--bk-offscreen-rows": rowsWorthRendering.from }} />
						)}
						{filteredTransactions.map((transaction, index) => {
							{
								if (index < rowsWorthRendering.from || index >= rowsWorthRendering.to) return null;

								let { date, description, amount, tax, classification, selected, accountName, confidence, cwClassified, glClassified, aiClassifying, canonical_code, canonical_label, gifi_code, classification_method } = transaction;
								let isSettledAsATransfer = rowsSettledAsTransfers.has(transaction);
								let isMatchedTransferLeg = !!transaction.mirrors_transaction_id;
								let odd = index % 2 === 0;
								let isUnacceptedSuspense = !!canonical_code && canonical_code.startsWith("SUSPENSE.") && !transaction.suspenseAccepted;
								let needsAttention = !isSettledAsATransfer && (BookkeepingService.stillNeedsAnAccount({ transaction }) || isUnacceptedSuspense);

								return (
										<React.Fragment key={index}>
									<div
										className={clsx("t__row", { "t__row--stripe": odd, "t__row--suspense": needsAttention })}
										key={index}
										onClick={() => this.handleSelectRow(transaction)}
									>
										<div className="t__row__item clients__row__item--checkbox">
											<label className={clsx("checkbox", "checkbox--small", "engs-row-item--checkbox")}>
												<input type="checkbox" checked={selected} readOnly />
											</label>
										</div>
										<div className="t__row__item bk-table__col--date">{date}</div>
										<div className="t__row__item bk-table__col--desc bk-table__col--openable" onClick={e => { e.stopPropagation(); this.update({ classifyModalTransaction: transaction }); }} title="Open — classify or mark as transfer">
											{description}
											{this.isLikelyDuplicate({ transaction, duplicateKeys }) && (
												<span className="bk-table__duplicate-flag" title="Another row has the same date, amount and description. Check whether it was imported twice — this is only a pointer, both may be genuine.">Possible duplicate</span>
											)}
										</div>
										<div className={clsx("t__row__item", "bk-table__col--amount", amount > 0 && "bk-table__amount--in")}>{UtilityService.formatCurrency(amount)}</div>
										{this.renderCurrencyCell({ transaction })}
										<div className="t__row__item bk-table__col--tax">
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : this.renderTaxValue(tax)}
									</div>
										<div className="t__row__item bk-table__col--tax-amt">
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : UtilityService.formatCurrency(this.calculateTaxAmount({ amount, tax }))}
									</div>
										<div
											className={clsx("t__row__item", "bk-table__classification-cell", "bk-table__col--classification", "bk-table__col--openable")}
											onClick={e => { e.stopPropagation(); this.update({ classifyModalTransaction: transaction }); }}
										>
											{aiClassifying ? (
												<span className="ai-classifying-dots">
													<span>.</span><span>.</span><span>.</span>
												</span>
											) : isMatchedTransferLeg ? (
												<span className="bk-table__matched-leg" title="One half of a transfer between the client's own accounts. The payment is recorded on the other leg; posting this one too would count it twice.">
													<Icon.Link2 size={12} />
													Transfer
												</span>
											) : (
												<span className="bk-table__classification-name">
													<span className={clsx("bk-table__class-dot", `bk-table__class-dot--${this.getClassificationDotType(transaction)}`)} />
													{canonical_code && <Icon.ChevronRight size={11} className={clsx("bk-table__classification-chevron", this.state.expandedClassificationRows.has(index) && "bk-table__classification-chevron--open")} />}
													{classification || "Unclassified"}
												</span>
											)}
										</div>
										<div
											className={clsx("t__row__item", "bk-table__canonical-cell", "bk-table__col--canonical", { "bk-table__canonical-cell--suspense": needsAttention })}
											title={canonical_code ? `${canonical_code}${gifi_code ? ` \u00B7 GIFI ${gifi_code}` : ""}` : ""}
										>
											{isMatchedTransferLeg ? "—" : (canonical_label || canonical_code || "—")}
										</div>
										<div className="t__row__item bk-table__col--statement">
											{isMatchedTransferLeg ? "—" : (BookkeepingService.whichStatementAnAccountSitsOn({ statementType: statementTypeByCanonicalCode[canonical_code] }) || "—")}
										</div>
										<div className="t__row__item bk-table__col--confidence">
										{aiClassifying ? (
											<span className="ai-classifying-dots">
												<span>.</span><span>.</span><span>.</span>
											</span>
										) : isMatchedTransferLeg ? "" : this.renderConfidence(confidence, cwClassified, glClassified)}
									</div>
										{this.renderReasonCell({ transaction })}
										<div className="t__row__item bk-table__col--account">{accountName}</div>
										<div className="t__row__item bk-table__action-cell bk-table__col--action">
											{transaction.isArchived ? (
												<span
													className="bk-table__action-icon"
													title="Restore to the books"
													onClick={e => { e.stopPropagation(); this.onRestoreRow(transaction); }}
												>
													<Icon.RotateCcw size={15} />
												</span>
											) : (
												<>
													{BookkeepingService.canAcceptClassification({ transaction }) && (
														<span
															className="bk-table__action-icon"
															title="Accept this classification"
															onClick={e => { e.stopPropagation(); this.onConfirmClassification(transaction); }}
														>
															<Icon.CheckCircle size={15} />
														</span>
													)}
													<span
														className="bk-table__action-icon bk-table__action-icon--archive"
														title="Archive — removes this from the books, keeps it under Archived"
														onClick={e => { e.stopPropagation(); this.openArchiveModalForRow(transaction); }}
													>
														<Icon.Archive size={14} />
													</span>
												</>
											)}
										</div>
									</div>
										{this.state.expandedClassificationRows.has(index) && canonical_code && (
											<div className="bk-table__cascade-row">
												<div className="bk-table__cascade-path">
													{canonical_code.split(".").map((segment, si, arr) => (
														<span key={si} className={clsx("bk-table__cascade-segment", si === arr.length - 1 && "bk-table__cascade-segment--active")}>
															{segment.replace(/_/g, " ")}
															{si === arr.length - 1 && gifi_code && <span className="bk-table__cascade-gifi">{gifi_code}</span>}
															{si < arr.length - 1 && <span className="bk-table__cascade-arrow">{"\u2192"}</span>}
														</span>
													))}
													{classification_method && (
														<span className="bk-table__cascade-source">
															{classification_method === "manual" ? "Preparer" : classification_method === "transfer" ? "Transfer" : classification_method === "gl_reference" || classification_method === "dbf_gifi" ? "General Ledger" : classification_method === "ai" ? "AI" : classification_method === "unmatched" ? "Unmatched" : classification_method}
														</span>
													)}
												</div>
												{this.renderClassificationReason(transaction)}
											</div>
										)}
										</React.Fragment>
								);
							}
						})}
						{filteredTransactions.length > rowsWorthRendering.to && (
							<div className="bk-table__offscreen-rows" style={{ "--bk-offscreen-rows": filteredTransactions.length - rowsWorthRendering.to }} />
						)}
					{(() => {
						const totalAmount = filteredTransactions.reduce((sum, t) => sum + (t.amount || 0), 0);
						const totalTaxAmount = filteredTransactions.reduce((sum, t) => sum + this.calculateTaxAmount({ amount: t.amount, tax: t.tax }), 0);
						return (
							<div className="t__row bk-table__totals-row">
								<div className="t__row__item clients__row__item--checkbox" />
								<div className="t__row__item bk-table__col--date" />
								<div className="t__row__item bk-table__col--desc bk-table__totals-count">{filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? "s" : ""}</div>
								<div className="t__row__item bk-table__col--amount">{UtilityService.formatCurrency(totalAmount)}</div>
								<div className="t__row__item bk-table__col--currency" />
								<div className="t__row__item bk-table__col--tax" />
								<div className="t__row__item bk-table__col--tax-amt">{UtilityService.formatCurrency(totalTaxAmount)}</div>
								<div className="t__row__item bk-table__col--classification" />
								<div className="t__row__item bk-table__col--canonical" />
								<div className="t__row__item bk-table__col--statement" />
								<div className="t__row__item bk-table__col--confidence" />
								<div className="t__row__item bk-table__col--reason" />
								<div className="t__row__item bk-table__col--account" />
								<div className="t__row__item bk-table__col--action" />
							</div>
						);
					})()}
					</div>
				</div>
				</>)}
				</div>
			</div>
		);
	}
}

export default BookkeepingTable;
